import React, { useEffect, useMemo, useState } from "react";
import { BrowserRouter, Link, NavLink, Route, Routes, useLocation, useNavigate, useParams } from "react-router-dom";
import clsx from "clsx";
import { format } from "date-fns";
import {
  ArrowRightIcon,
  ClipboardDocumentCheckIcon,
  GlobeAltIcon,
  TruckIcon,
  PaperAirplaneIcon,
  LockClosedIcon,
  DocumentArrowUpIcon,
} from "@heroicons/react/24/outline";

import "./App.css";

const ENV_BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// In production (combined Railway service), the API is served from the same origin.
// In local dev, the CRA dev server runs on localhost:3000, so we use REACT_APP_BACKEND_URL.
const BACKEND_URL = (() => {
  if (typeof window === "undefined") return ENV_BACKEND_URL || "";
  const origin = window.location.origin;
  if (origin.includes("localhost")) return ENV_BACKEND_URL || origin;
  return origin;
})();


// Marketing images (Unsplash). Kept as constants so they’re easy to swap later.
// NOTE: In production you may want to self-host or use a CDN with proper licensing.
const IMG_HERO = "https://images.unsplash.com/photo-1590497008432-598f04441de8?auto=format&fit=crop&w=2400&q=80";
const IMG_AIR = "https://images.unsplash.com/photo-1571086291540-b137111fa1c7?auto=format&fit=crop&w=1600&q=80";
const IMG_SEA = "https://images.unsplash.com/photo-1670121180530-cfcba4438038?auto=format&fit=crop&w=1600&q=80";
const IMG_WAREHOUSE = "https://images.unsplash.com/photo-1761307234387-d9291985eaf9?auto=format&fit=crop&w=1600&q=80";

function apiUrl(path) {
  if (!BACKEND_URL) return null;
  const base = BACKEND_URL.replace(/\/$/, "");
  const p = path.startsWith("/") ? path : `/${path}`;
  return `${base}${p}`;
}


function useDemoTracking() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    apiFetch("/api/demo/tracking")
      .then((d) => mounted && setItems(d.items || []))
      .catch(() => mounted && setItems([]))
      .finally(() => mounted && setLoading(false));
    return () => {
      mounted = false;
    };
  }, []);

  return { items, loading };
}

async function apiFetch(path, { method = "GET", token, body } = {}) {
  const url = apiUrl(path);
  if (!url) throw new Error("REACT_APP_BACKEND_URL is not set");

  const res = await fetch(url, {
    method,
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: body ? JSON.stringify(body) : undefined,
  });

  const text = await res.text();
  const data = text ? JSON.parse(text) : null;
  if (!res.ok) {
    let message = (data && (data.detail || data.message)) || `Request failed (${res.status})`;

    // FastAPI validation errors return: { detail: [{ loc: [...], msg: "...", type: "..." }, ...] }
    if (Array.isArray(message)) {
      message = message
        .map((item) => {
          const loc = Array.isArray(item.loc) ? item.loc.join(".") : "";
          const msg = item.msg || "Invalid input";
          return loc ? `${loc}: ${msg}` : msg;
        })
        .join("; ");
    }

    // If token becomes invalid after a deploy (e.g., JWT secret rotated), force logout.
    if (typeof window !== "undefined" && res.status === 401) {
      try {
        localStorage.removeItem("phl_admin_token");
        localStorage.removeItem("phl_admin_email");
      } catch {
        // ignore
      }
      window.location.href = "/admin?session=expired";
    }

    const err = new Error(message);
    err.status = res.status;
    err.data = data;
    throw err;
  }
  return data;
}

function useAuth() {
  const [token, setToken] = useState(() => localStorage.getItem("phl_admin_token") || "");
  const [adminEmail, setAdminEmail] = useState(() => localStorage.getItem("phl_admin_email") || "");

  const isAuthed = !!token;

  const login = async (email, password) => {
    const data = await apiFetch("/api/auth/login", { method: "POST", body: { email, password } });
    localStorage.setItem("phl_admin_token", data.token);
    localStorage.setItem("phl_admin_email", data.adminEmail);
    setToken(data.token);
    setAdminEmail(data.adminEmail);
  };

  const logout = () => {
    localStorage.removeItem("phl_admin_token");
    localStorage.removeItem("phl_admin_email");
    setToken("");
    setAdminEmail("");
  };

  return { token, adminEmail, isAuthed, login, logout };
}

function Container({ children, className }) {
  return <div className={clsx("mx-auto w-full max-w-6xl px-4", className)}>{children}</div>;
}

function PrimaryButton({ children, className, ...props }) {
  return (
    <button
      className={clsx(
        "inline-flex items-center justify-center gap-2 rounded-xl bg-harbor-700 px-4 py-2.5 text-sm font-semibold text-white shadow-sm",
        "hover:bg-harbor-600 focus:outline-none focus:ring-2 focus:ring-harbor-600/30 disabled:opacity-60",
        className
      )}
      {...props}
    >
      {children}
    </button>
  );
}

function IconButton({ children, className, ...props }) {
  return (
    <button
      className={clsx(
        "inline-flex items-center justify-center rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-900 shadow-sm",
        "hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-harbor-600/20 disabled:opacity-60",
        className
      )}
      {...props}
    >
      {children}
    </button>
  );
}

function SecondaryButton({ children, className, ...props }) {
  return (
    <button
      className={clsx(
        "inline-flex items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-900 shadow-sm",
        "hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-harbor-600/20 disabled:opacity-60",
        className
      )}
      {...props}
    >
      {children}
    </button>
  );
}

function Input({ className, ...props }) {
  return (
    <input
      className={clsx(
        "w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 shadow-sm",
        "placeholder:text-slate-400 focus:border-harbor-600 focus:outline-none focus:ring-2 focus:ring-harbor-600/20",
        className
      )}
      {...props}
    />
  );
}

function Select({ className, ...props }) {
  return (
    <select
      className={clsx(
        "w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 shadow-sm",
        "focus:border-harbor-600 focus:outline-none focus:ring-2 focus:ring-harbor-600/20",
        className
      )}
      {...props}
    />
  );
}

function TextArea({ className, ...props }) {
  return (
    <textarea
      className={clsx(
        "w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 shadow-sm",
        "placeholder:text-slate-400 focus:border-harbor-600 focus:outline-none focus:ring-2 focus:ring-harbor-600/20",
        className
      )}
      {...props}
    />
  );
}

function Badge({ status }) {
  const map = {
    LABEL_CREATED: "bg-slate-100 text-slate-800",
    PICKED_UP: "bg-indigo-50 text-indigo-700",
    IN_TRANSIT: "bg-blue-50 text-blue-700",
    ARRIVED_AT_HUB: "bg-amber-50 text-amber-700",
    CUSTOMS: "bg-yellow-50 text-yellow-800",
    OUT_FOR_DELIVERY: "bg-cyan-50 text-cyan-700",
    DELIVERED: "bg-emerald-50 text-emerald-700",
    EXCEPTION: "bg-rose-50 text-rose-700",
    PACKAGE_ON_HOLD: "bg-orange-50 text-orange-700",
  };
  return (
    <span
      data-testid="status-badge"
      className={clsx(
        "inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold",
        map[status] || "bg-slate-100 text-slate-800"
      )}
    >
      {status.replaceAll("_", " ")}
    </span>
  );
}

function TopNav({ auth }) {
  return (
    <div className="sticky top-0 z-40 border-b border-slate-200 bg-white/85 backdrop-blur">
      <Container className="flex h-16 items-center justify-between">
        <Link to="/" className="flex items-center gap-2" data-testid="nav-brand-link">
          <div className="grid h-9 w-9 place-items-center rounded-xl bg-ink-900 text-white">
            <GlobeAltIcon className="h-5 w-5" />
          </div>
          <div className="leading-tight">
            <div className="text-sm font-extrabold text-slate-900">PrimeHarbor</div>
            <div className="text-xs font-medium text-slate-500">Logistics</div>
          </div>
        </Link>

        <div className="hidden items-center gap-6 md:flex">
          <NavLink
            to="/services"
            className={({ isActive }) =>
              clsx(
                "text-sm font-semibold",
                isActive ? "text-harbor-700" : "text-slate-700 hover:text-slate-900"
              )
            }
            data-testid="nav-services-link"
          >
            Services
          </NavLink>
          <NavLink
            to="/track"
            className={({ isActive }) =>
              clsx(
                "text-sm font-semibold",
                isActive ? "text-harbor-700" : "text-slate-700 hover:text-slate-900"
              )
            }
            data-testid="nav-track-link"
          >
            Track
          </NavLink>
          <NavLink
            to="/quote"
            className={({ isActive }) =>
              clsx(
                "text-sm font-semibold",
                isActive ? "text-harbor-700" : "text-slate-700 hover:text-slate-900"
              )
            }
            data-testid="nav-quote-link"
          >
            Get a Quote
          </NavLink>
          <NavLink
            to="/about"
            className={({ isActive }) =>
              clsx(
                "text-sm font-semibold",
                isActive ? "text-harbor-700" : "text-slate-700 hover:text-slate-900"
              )
            }
            data-testid="nav-about-link"
          >
            About
          </NavLink>
          <NavLink
            to="/contact"
            className={({ isActive }) =>
              clsx(
                "text-sm font-semibold",
                isActive ? "text-harbor-700" : "text-slate-700 hover:text-slate-900"
              )
            }
            data-testid="nav-contact-link"
          >
            Contact
          </NavLink>
        </div>

        <div className="flex items-center gap-2">
          {auth?.isAuthed ? (
            <SecondaryButton onClick={auth.logout} data-testid="nav-admin-logout-button">
              Logout
            </SecondaryButton>
          ) : null}
        </div>
      </Container>
    </div>
  );
}

function Footer() {
  return (
    <div className="border-t border-slate-200 bg-white">
      <Container className="py-10">
        <div className="grid gap-8 md:grid-cols-4">
          <div>
            <div className="text-sm font-extrabold text-slate-900">PrimeHarbor Logistics</div>
            <div className="mt-2 text-sm text-slate-600">
              Worldwide shipping solutions across air, sea, and road — with enterprise-grade tracking.
            </div>
          </div>
          <div>
            <div className="text-sm font-bold text-slate-900">Company</div>
            <div className="mt-3 space-y-2 text-sm">
              <Link className="block text-slate-600 hover:text-slate-900" to="/about" data-testid="footer-about-link">
                About
              </Link>
              <Link className="block text-slate-600 hover:text-slate-900" to="/contact" data-testid="footer-contact-link">
                Contact
              </Link>
              <Link className="block text-slate-600 hover:text-slate-900" to="/services" data-testid="footer-services-link">
                Services
              </Link>
            </div>
          </div>
          <div>
            <div className="text-sm font-bold text-slate-900">Legal</div>
            <div className="mt-3 space-y-2 text-sm">
              <Link className="block text-slate-600 hover:text-slate-900" to="/privacy" data-testid="footer-privacy-link">
                Privacy
              </Link>
              <Link className="block text-slate-600 hover:text-slate-900" to="/terms" data-testid="footer-terms-link">
                Terms
              </Link>
            </div>
          </div>
          <div>
            <div className="text-sm font-bold text-slate-900">Support</div>
            <div className="mt-3 space-y-2 text-sm">
              <div className="text-slate-600">Email: support@primeharborlogistics.group</div>
              <div className="text-slate-600" data-testid="footer-support-email-alt">Email: primeharborlogistics@gmail.com</div>

              <Link className="block font-semibold text-slate-700 hover:text-slate-900" to="/admin" data-testid="footer-admin-link">
                Admin Portal
              </Link>
            </div>
          </div>
        </div>
        <div className="mt-10 text-xs text-slate-500">© {new Date().getFullYear()} PrimeHarbor Logistics. All rights reserved.</div>
      </Container>
    </div>
  );
}

function TrackingInput({ initial = "", onSubmit, size = "md" }) {
  const [value, setValue] = useState(initial);
  const [error, setError] = useState("");

  const inputClass = size === "lg" ? "py-3 text-base" : "";

  return (
    <div>
      <div className="flex flex-col gap-2 md:flex-row">
        <Input
          data-testid="tracking-id-input"
          value={value}
          onChange={(e) => {
            setValue(e.target.value);
            setError("");
          }}
          placeholder="Enter Tracking ID (e.g., PHL-2026-4F9A2C)"
          className={clsx("md:flex-1", inputClass)}
        />
        <PrimaryButton
          data-testid="tracking-submit-button"
          onClick={() => {
            const v = value.trim().toUpperCase();
            if (!v) {
              setError("Please enter a tracking ID");
              return;
            }
            onSubmit(v);
          }}
          className={clsx(size === "lg" ? "py-3" : "")}
        >
          Track <ArrowRightIcon className="h-4 w-4" />
        </PrimaryButton>
      </div>
      {error ? (
        <div className="mt-2 text-sm text-rose-600" data-testid="tracking-error-message">
          {error}
        </div>
      ) : null}
    </div>
  );
}

function HomePage() {
  const navigate = useNavigate();
  const demo = useDemoTracking();

  return (
    <div data-testid="homepage">
      {/* HERO */}
      <div className="relative overflow-hidden bg-ink-900">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${IMG_HERO})` }}
          aria-hidden="true"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-ink-900/80 via-ink-900/70 to-ink-900/20" aria-hidden="true" />
        <div className="absolute inset-0 ring-1 ring-inset ring-white/10" aria-hidden="true" />

        <Container className="relative py-16 md:py-20">
          <div className="grid gap-10 md:grid-cols-2 md:items-center">
            <div>
              <div
                className="inline-flex items-center gap-2 rounded-full bg-white/10 px-3 py-1 text-xs font-semibold text-white ring-1 ring-white/10"
                data-testid="hero-badge"
              >
                Worldwide Coverage • 24/7 Visibility • Dedicated Support
              </div>
              <h1 className="mt-4 text-4xl font-extrabold tracking-tight text-white md:text-5xl" data-testid="hero-title">
                PrimeHarbor Logistics
                <span className="block text-white/85">Global freight with enterprise tracking.</span>
              </h1>
              <p className="mt-4 text-base leading-relaxed text-white/80" data-testid="hero-subtitle">
                Ship parcels and freight worldwide via air, sea, and road. Get predictable milestones, clean documentation,
                and an audit-ready tracking timeline your customers can trust.
              </p>

              <div className="mt-8 rounded-2xl bg-white/10 p-4 shadow-sm ring-1 ring-white/15 backdrop-blur" data-testid="hero-tracking-card">
                <div className="mb-2 text-sm font-semibold text-white">Track a shipment</div>
                <TrackingInput onSubmit={(tid) => navigate(`/track/${encodeURIComponent(tid)}`)} size="lg" />
                <div className="mt-3 flex flex-wrap gap-3 text-sm">
                  <Link
                    to="/quote"
                    className="inline-flex items-center gap-2 text-white/85 hover:text-white"
                    data-testid="hero-cta-quote-link"
                  >
                    <PaperAirplaneIcon className="h-4 w-4" /> Get a Quote
                  </Link>
                  <Link
                    to="/services"
                    className="inline-flex items-center gap-2 text-white/85 hover:text-white"
                    data-testid="hero-cta-services-link"
                  >
                    <TruckIcon className="h-4 w-4" /> Explore services
                  </Link>
                </div>
              </div>

              <div className="mt-6 grid gap-3 md:grid-cols-3" data-testid="hero-metrics">
                <HeroMetric label="Global lanes" value="120+" testId="hero-metric-lanes" />
                <HeroMetric label="Countries" value="200+" testId="hero-metric-countries" />
                <HeroMetric label="Avg. response" value="< 2 hrs" testId="hero-metric-response" />
              </div>

              <div className="mt-6 rounded-2xl bg-white/10 p-4 ring-1 ring-white/10" data-testid="demo-tracking-panel">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <div className="text-xs font-semibold text-white/70">Try a demo tracking ID</div>
                    <div className="mt-1 text-sm font-bold text-white">Live sample shipments from our system</div>
                  </div>
                  <Link to="/track" className="text-sm font-semibold text-white/85 hover:text-white" data-testid="demo-tracking-view-all-link">
                    Track search →
                  </Link>
                </div>

                <div className="mt-4 grid gap-3 md:grid-cols-3" data-testid="demo-tracking-grid">
                  {demo.loading ? (
                    <div className="text-sm text-white/75" data-testid="demo-tracking-loading">Loading demo tracking IDs…</div>
                  ) : demo.items.length === 0 ? (
                    <div className="text-sm text-white/75" data-testid="demo-tracking-empty">No demo shipments available.</div>
                  ) : (
                    demo.items.slice(0, 3).map((it, idx) => (
                      <Link
                        key={it.trackingId}
                        to={`/track/${encodeURIComponent(it.trackingId)}`}
                        className="rounded-xl bg-white/10 p-3 ring-1 ring-white/10 hover:bg-white/15"
                        data-testid={`demo-tracking-item-${idx}`}
                      >
                        <div className="font-mono text-sm font-extrabold text-white" data-testid={`demo-tracking-id-${idx}`}>{it.trackingId}</div>
                        <div className="mt-1 text-xs text-white/75" data-testid={`demo-tracking-route-${idx}`}>{it.origin} → {it.destination}</div>
                        <div className="mt-2 flex items-center justify-between gap-2">
                          <span className="rounded-full bg-white/10 px-2 py-0.5 text-[11px] font-bold text-white/85" data-testid={`demo-tracking-status-${idx}`}>{it.currentStatus.replaceAll("_", " ")}</span>
                          <span className="text-[11px] font-semibold text-white/70" data-testid={`demo-tracking-mode-${idx}`}>{it.mode}</span>
                        </div>
                      </Link>
                    ))
                  )}
                </div>
              </div>
            </div>

            <div className="grid gap-4">
              <div className="rounded-2xl bg-white/95 p-6 shadow-sm ring-1 ring-black/5" data-testid="hero-feature-card-1">
                <div className="flex items-center gap-3">
                  <div className="grid h-10 w-10 place-items-center rounded-xl bg-harbor-700 text-white">
                    <ClipboardDocumentCheckIcon className="h-5 w-5" />
                  </div>
                  <div>
                    <div className="text-sm font-bold text-slate-900">Milestone timeline</div>
                    <div className="text-sm text-slate-600">DHL/FedEx-style events, newest-first.</div>
                  </div>
                </div>
              </div>

              <div className="rounded-2xl bg-white/95 p-6 shadow-sm ring-1 ring-black/5" data-testid="hero-feature-card-2">
                <div className="flex items-center gap-3">
                  <div className="grid h-10 w-10 place-items-center rounded-xl bg-ink-900 text-white">
                    <GlobeAltIcon className="h-5 w-5" />
                  </div>
                  <div>
                    <div className="text-sm font-bold text-slate-900">Worldwide validation</div>
                    <div className="text-sm text-slate-600">Origin/destination country validation.</div>
                  </div>
                </div>
              </div>

              <div className="rounded-2xl bg-white/95 p-6 shadow-sm ring-1 ring-black/5" data-testid="hero-feature-card-3">
                <div className="flex items-center gap-3">
                  <div className="grid h-10 w-10 place-items-center rounded-xl bg-emerald-600 text-white">
                    <TruckIcon className="h-5 w-5" />
                  </div>
                  <div>
                    <div className="text-sm font-bold text-slate-900">Multi-modal shipping</div>
                    <div className="text-sm text-slate-600">Air, sea, and road — parcels and freight.</div>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-3 gap-3" data-testid="hero-image-strip">
                <MiniImage src={IMG_AIR} alt="Air freight" testId="hero-image-air" />
                <MiniImage src={IMG_SEA} alt="Sea freight" testId="hero-image-sea" />
                <MiniImage src={IMG_WAREHOUSE} alt="Warehouse" testId="hero-image-warehouse" />
              </div>
            </div>
          </div>
        </Container>
      </div>

      {/* SERVICES */}
      <div className="bg-white">
        <Container className="py-14">
          <div className="flex flex-col gap-2 md:flex-row md:items-end md:justify-between" data-testid="home-services-header">
            <div>
              <div className="text-xs font-semibold text-slate-500">Services</div>
              <div className="text-2xl font-extrabold text-slate-900" data-testid="home-services-title">Global shipping built for operations</div>
              <p className="mt-2 max-w-2xl text-sm text-slate-600" data-testid="home-services-subtitle">
                From customs paperwork to last-mile delivery, PrimeHarbor gives you structured visibility across the full journey.
              </p>
            </div>
            <Link to="/services" data-testid="home-services-view-all-link" className="text-sm font-semibold text-harbor-700 hover:text-harbor-800">
              View all services →
            </Link>
          </div>

          <div className="mt-8 grid gap-6 md:grid-cols-3" data-testid="services-cards">
            <ServiceCard icon={<PaperAirplaneIcon className="h-5 w-5" />} title="Air Freight" desc="Priority uplift, reliable ETAs, proactive status updates." image={IMG_AIR} to="/services/air-freight" />
            <ServiceCard icon={<GlobeAltIcon className="h-5 w-5" />} title="Sea Freight" desc="FCL/LCL, port handling, and milestone tracking across lanes." image={IMG_SEA} to="/services/sea-freight" />
            <ServiceCard icon={<TruckIcon className="h-5 w-5" />} title="Road Freight" desc="Regional linehaul and last-mile coordination with proof points." to="/services/road-freight" />
            <ServiceCard icon={<ClipboardDocumentCheckIcon className="h-5 w-5" />} title="Customs" desc="Documents, compliance checks, and broker coordination." to="/services/customs" />
            <ServiceCard icon={<DocumentArrowUpIcon className="h-5 w-5" />} title="Warehousing" desc="Secure storage, consolidation, and dispatch operations." image={IMG_WAREHOUSE} to="/services/warehousing" />
            {/* Secure Admin marketing page removed (not needed) */}
          </div>

          <div className="mt-14 grid gap-10 rounded-2xl border border-slate-200 bg-slate-50 p-8 md:grid-cols-2" data-testid="how-it-works">
            <div>
              <div className="text-xs font-semibold text-slate-500">How it works</div>
              <div className="mt-2 text-xl font-extrabold text-slate-900">Operational clarity, step by step</div>
              <ol className="mt-4 space-y-3 text-sm text-slate-700">
                <li className="flex gap-3"><span className="mt-0.5 inline-flex h-6 w-6 items-center justify-center rounded-full bg-harbor-700 text-white text-xs font-bold">1</span> Request a quote with route and cargo details.</li>
                <li className="flex gap-3"><span className="mt-0.5 inline-flex h-6 w-6 items-center justify-center rounded-full bg-harbor-700 text-white text-xs font-bold">2</span> Admin creates a shipment and tracking ID is generated.</li>
                <li className="flex gap-3"><span className="mt-0.5 inline-flex h-6 w-6 items-center justify-center rounded-full bg-harbor-700 text-white text-xs font-bold">3</span> Events are posted as the shipment clears hubs and customs.</li>
                <li className="flex gap-3"><span className="mt-0.5 inline-flex h-6 w-6 items-center justify-center rounded-full bg-harbor-700 text-white text-xs font-bold">4</span> Customers track via public link — no login required.</li>
              </ol>
            </div>
            <div className="rounded-2xl bg-white p-6 shadow-sm ring-1 ring-black/5" data-testid="how-it-works-card">
              <div className="text-sm font-bold text-slate-900">Ready to ship?</div>
              <p className="mt-2 text-sm text-slate-600">Get started with a quote request, or track an existing shipment.</p>
              <div className="mt-6 flex flex-wrap gap-3">
                <Link to="/quote" data-testid="how-it-works-quote-link">
                  <PrimaryButton>Get a Quote <ArrowRightIcon className="h-4 w-4" /></PrimaryButton>
                </Link>
                <Link to="/track" data-testid="how-it-works-track-link">
                  <SecondaryButton>Track Shipment</SecondaryButton>
                </Link>
              </div>

              <div className="mt-6 rounded-xl bg-slate-50 p-4" data-testid="how-it-works-copy">
                <div className="text-xs font-semibold text-slate-500">What you get</div>
                <ul className="mt-2 space-y-2 text-sm text-slate-700">
                  <li>• Latest status + last update timestamp</li>
                  <li>• Location-based timeline events</li>
                  <li>• Cloud documents (invoice, B/L, packing list)</li>
                </ul>
              </div>
            </div>
          </div>
        </Container>
      </div>
    </div>
  );
}

function HeroMetric({ label, value, testId }) {
  return (
    <div className="rounded-2xl bg-white/10 px-4 py-3 ring-1 ring-white/10" data-testid={testId}>
      <div className="text-xs font-semibold text-white/70" data-testid={`${testId}-label`}>{label}</div>
      <div className="mt-1 text-lg font-extrabold text-white" data-testid={`${testId}-value`}>{value}</div>
    </div>
  );
}

function MiniImage({ src, alt, testId }) {
  return (
    <div className="relative overflow-hidden rounded-xl ring-1 ring-white/10" data-testid={testId}>
      <img src={src} alt={alt} className="h-20 w-full object-cover" loading="lazy" />
      <div className="absolute inset-0 bg-gradient-to-t from-ink-900/50 to-transparent" />
    </div>
  );
}

function ServiceCard({ icon, title, desc, image, to }) {
  const content = (
    <div
      className="group overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm transition hover:-translate-y-0.5 hover:shadow-md"
      data-testid={`service-card-${title.toLowerCase().replaceAll(" ", "-")}`}
    >
      {image ? (
        <div className="relative h-36 overflow-hidden" data-testid={`service-card-image-${title.toLowerCase().replaceAll(" ", "-")}`}>
          <img
            src={image}
            alt={title}
            className="h-full w-full object-cover transition duration-500 group-hover:scale-[1.03]"
            loading="lazy"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-ink-900/45 via-ink-900/10 to-transparent" />
        </div>
      ) : null}

      <div className="p-6">
        <div className="flex items-start gap-3">
          <div className="grid h-10 w-10 place-items-center rounded-xl bg-ink-900 text-white">{icon}</div>
          <div>
            <div className="text-sm font-bold text-slate-900">{title}</div>
            <div className="mt-1 text-sm text-slate-600">{desc}</div>
          </div>
        </div>

        <div className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-harbor-700" data-testid={`service-card-cta-${title.toLowerCase().replaceAll(" ", "-")}`}>
          Learn more <ArrowRightIcon className="h-4 w-4" />
        </div>
      </div>
    </div>
  );

  return to ? (
    <Link to={to} className="block focus:outline-none focus:ring-2 focus:ring-harbor-600/20 rounded-2xl">
      {content}
    </Link>
  ) : (
    content
  );
}

function ServiceDetail({ title, image, bullets, testId }) {
  return (
    <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm" data-testid={testId}>
      <div className="relative h-44 overflow-hidden">
        <img src={image} alt={title} className="h-full w-full object-cover" loading="lazy" />
        <div className="absolute inset-0 bg-gradient-to-t from-ink-900/55 via-ink-900/10 to-transparent" />
        <div className="absolute bottom-3 left-3 rounded-xl bg-white/10 px-3 py-1 text-sm font-extrabold text-white ring-1 ring-white/10 backdrop-blur" data-testid={`${testId}-title`}>
          {title}
        </div>
      </div>
      <div className="p-6">
        <ul className="space-y-2 text-sm text-slate-700" data-testid={`${testId}-bullets`}>
          {bullets.map((b, idx) => (
            <li key={idx} className="flex gap-2" data-testid={`${testId}-bullet-${idx}`}>
              <span className="mt-2 inline-block h-1.5 w-1.5 rounded-full bg-harbor-700" />
              <span>{b}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

function TrustCard({ title, desc, testId }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm" data-testid={testId}>
      <div className="text-sm font-extrabold text-slate-900" data-testid={`${testId}-title`}>{title}</div>
      <div className="mt-2 text-sm text-slate-600" data-testid={`${testId}-desc`}>{desc}</div>
    </div>
  );
}

function Stat({ label, value, testId }) {
  return (
    <div className="rounded-2xl bg-slate-50 p-6" data-testid={testId}>
      <div className="text-xs font-semibold text-slate-500" data-testid={`${testId}-label`}>{label}</div>
      <div className="mt-2 text-2xl font-extrabold text-slate-900" data-testid={`${testId}-value`}>{value}</div>
    </div>
  );
}

function FaqItem({ q, a, testId }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm" data-testid={testId}>
      <button
        className="flex w-full items-center justify-between gap-3 text-left"
        onClick={() => setOpen((v) => !v)}
        type="button"
        data-testid={`${testId}-toggle`}
      >
        <div className="text-sm font-extrabold text-slate-900" data-testid={`${testId}-q`}>{q}</div>
        <span className="text-sm font-bold text-harbor-700" aria-hidden="true">
          {open ? "−" : "+"}
        </span>
      </button>
      {open ? (
        <div className="mt-3 text-sm text-slate-600" data-testid={`${testId}-a`}>
          {a}
        </div>
      ) : null}
    </div>
  );
}


function TrackSearchPage() {
  const navigate = useNavigate();
  const demo = useDemoTracking();

  return (
    <div data-testid="track-search-page">
      <div className="relative overflow-hidden bg-ink-900">
        <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${IMG_HERO})` }} aria-hidden="true" />
        <div className="absolute inset-0 bg-gradient-to-b from-ink-900/85 via-ink-900/75 to-ink-900/35" aria-hidden="true" />
        <Container className="relative py-14">
          <div className="max-w-3xl">
            <div className="text-xs font-semibold text-white/70" data-testid="track-search-kicker">Tracking</div>
            <h2 className="mt-2 text-3xl font-extrabold tracking-tight text-white md:text-4xl" data-testid="track-search-title">
              Track your shipment in real time.
            </h2>
            <p className="mt-3 text-sm leading-relaxed text-white/80" data-testid="track-search-subtitle">
              Enter your tracking ID to view the latest status, locations, and milestone timeline.
            </p>

            <div className="mt-6 max-w-2xl rounded-2xl bg-white/10 p-4 ring-1 ring-white/10 backdrop-blur" data-testid="track-search-input-card">
              <TrackingInput onSubmit={(tid) => navigate(`/track/${encodeURIComponent(tid)}`)} size="lg" />
              <div className="mt-2 text-xs text-white/70" data-testid="track-search-hint">
                Example format: <span className="font-mono font-bold text-white/80">PHL-2026-4F9A2C</span>
              </div>
            </div>
          </div>
        </Container>
      </div>

      <Container className="py-14">
        <div className="grid gap-8 md:grid-cols-[1fr_360px]" data-testid="track-search-layout">
          <div className="rounded-2xl border border-slate-200 bg-white p-8 shadow-sm" data-testid="track-search-info">
            <div className="text-xs font-semibold text-slate-500">What you’ll see</div>
            <div className="mt-2 text-2xl font-extrabold text-slate-900">Shipment summary + event timeline</div>
            <p className="mt-2 text-sm text-slate-600">
              We surface the latest milestone first (newest-first), including timestamp, location, and status notes.
            </p>
            <div className="mt-6 grid gap-4 md:grid-cols-3" data-testid="track-search-info-cards">
              <TrustCard title="Current status" desc="Derived from the latest event." testId="track-search-card-status" />
              <TrustCard title="Route" desc="Origin and destination visibility." testId="track-search-card-route" />
              <TrustCard title="Timeline" desc="Enterprise-style milestone history." testId="track-search-card-timeline" />
            </div>
          </div>

          <div className="rounded-2xl border border-slate-200 bg-slate-50 p-6" data-testid="track-search-demo">
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="text-xs font-semibold text-slate-500">Demo</div>
                <div className="mt-1 text-sm font-extrabold text-slate-900">Try a sample tracking ID</div>
              </div>
              <Link to="/" className="text-sm font-semibold text-harbor-700 hover:text-harbor-800" data-testid="track-search-demo-home-link">
                Home →
              </Link>
            </div>

            <div className="mt-4 space-y-3" data-testid="track-search-demo-list">
              {demo.loading ? (
                <div className="text-sm text-slate-600" data-testid="track-search-demo-loading">Loading…</div>
              ) : demo.items.length === 0 ? (
                <div className="text-sm text-slate-600" data-testid="track-search-demo-empty">No demo shipments available.</div>
              ) : (
                demo.items.slice(0, 3).map((it, idx) => (
                  <Link
                    key={it.trackingId}
                    to={`/track/${encodeURIComponent(it.trackingId)}`}
                    className="block rounded-xl border border-slate-200 bg-white p-4 shadow-sm hover:shadow-md"
                    data-testid={`track-search-demo-item-${idx}`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <div className="font-mono text-sm font-extrabold text-slate-900" data-testid={`track-search-demo-id-${idx}`}>{it.trackingId}</div>
                        <div className="mt-1 text-xs text-slate-500" data-testid={`track-search-demo-route-${idx}`}>{it.origin} → {it.destination}</div>
                      </div>
                      <span className="rounded-full bg-slate-100 px-2 py-1 text-xs font-bold text-slate-700" data-testid={`track-search-demo-mode-${idx}`}>{it.mode}</span>
                    </div>
                    <div className="mt-3">
                      <span className="rounded-full bg-harbor-50 px-2 py-1 text-xs font-bold text-harbor-800" data-testid={`track-search-demo-status-${idx}`}>{it.currentStatus.replaceAll("_", " ")}</span>
                    </div>
                  </Link>
                ))
              )}
            </div>
          </div>
        </div>
      </Container>
    </div>
  );
}

function TrackResultPage() {
  const { trackingId } = useParams();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    let mounted = true;
    async function run() {
      setLoading(true);
      setError("");
      setData(null);
      try {
        const resp = await apiFetch(`/api/track?trackingId=${encodeURIComponent(trackingId)}`);
        if (mounted) setData(resp);
      } catch (e) {
        if (mounted) setError(e.message || "Tracking ID not found");
      } finally {
        if (mounted) setLoading(false);
      }
    }
    run();
    return () => {
      mounted = false;
    };
  }, [trackingId]);

  return (
    <div data-testid="track-result-page">
      <div className="relative overflow-hidden bg-ink-900">
        <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${IMG_SEA})` }} aria-hidden="true" />
        <div className="absolute inset-0 bg-gradient-to-b from-ink-900/85 via-ink-900/75 to-ink-900/35" aria-hidden="true" />
        <Container className="relative py-10">
          <div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between" data-testid="track-result-hero">
            <div>
              <div className="text-xs font-semibold text-white/70" data-testid="track-result-label">Tracking</div>
              <h2 className="mt-2 font-mono text-2xl font-extrabold tracking-tight text-white md:text-3xl" data-testid="track-result-title">
                {trackingId}
              </h2>
              <div className="mt-2 text-sm text-white/75" data-testid="track-result-hero-subtitle">
                Shipment summary and milestone history (newest-first)
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              <Link to="/track" data-testid="track-result-new-search-link">
                <SecondaryButton>New search</SecondaryButton>
              </Link>
              <SecondaryButton
                data-testid="track-result-copy-link-button"
                onClick={() => {
                  try {
                    navigator.clipboard.writeText(window.location.href);
                  } catch {
                    // ignore
                  }
                }}
              >
                Copy link
              </SecondaryButton>
            </div>
          </div>
        </Container>
      </div>

      <Container className="py-14">
        {loading ? (
          <div className="rounded-2xl border border-slate-200 bg-white p-6" data-testid="track-loading">
            <div className="h-5 w-40 animate-pulse rounded bg-slate-100" />
            <div className="mt-4 h-4 w-64 animate-pulse rounded bg-slate-100" />
            <div className="mt-2 h-4 w-52 animate-pulse rounded bg-slate-100" />
          </div>
        ) : error ? (
          <div className="rounded-2xl border border-rose-200 bg-rose-50 p-8" data-testid="track-error">
            <div className="text-sm font-extrabold text-rose-800">Tracking ID not found</div>
            <div className="mt-2 text-sm text-rose-700">{error}</div>
            <div className="mt-6 flex gap-3">
              <Link to="/track" data-testid="track-retry-link">
                <SecondaryButton>Try another</SecondaryButton>
              </Link>
              <Link to="/" data-testid="track-back-home-link">
                <SecondaryButton>Back to home</SecondaryButton>
              </Link>
            </div>
          </div>
        ) : (
          <div className="grid gap-6 md:grid-cols-[1fr_420px]" data-testid="track-result-layout">
            <div className="space-y-6" data-testid="track-result-left">
              <ShipmentSummary shipment={data.shipment} />
              <Timeline events={data.events} />
            </div>
            <div className="space-y-6" data-testid="track-result-right">
              <div className="rounded-2xl border border-slate-200 bg-slate-50 p-6" data-testid="track-result-support">
                <div className="text-sm font-extrabold text-slate-900">Need help?</div>
                <div className="mt-2 text-sm text-slate-600">
                  If your shipment shows an exception or customs hold, contact support and include this tracking ID.
                </div>
                <div className="mt-4 space-y-2 text-sm text-slate-700">
                  <div data-testid="track-result-support-email">Email: <span className="font-semibold">support@primeharborlogistics.group</span></div>
                  <div data-testid="track-result-support-email-alt">Email: <span className="font-semibold">primeharborlogistics@gmail.com</span></div>

                </div>
              </div>

              <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm" data-testid="track-result-cta">
                <div className="relative h-40">
                  <img src={IMG_WAREHOUSE} alt="Warehouse" className="h-full w-full object-cover" loading="lazy" />
                  <div className="absolute inset-0 bg-gradient-to-t from-ink-900/60 to-transparent" />
                </div>
                <div className="p-6">
                  <div className="text-sm font-extrabold text-slate-900">Shipping something new?</div>
                  <div className="mt-2 text-sm text-slate-600">Request a quote for your next lane and receive pricing + recommended mode.</div>
                  <div className="mt-4">
                    <Link to="/quote" data-testid="track-result-quote-link"><PrimaryButton className="w-full">Get a Quote</PrimaryButton></Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </Container>
    </div>
  );
}

function ShipmentSummary({ shipment }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm" data-testid="shipment-summary-card">
      <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
        <div>
          <div className="text-sm text-slate-500">Current Status</div>
          <div className="mt-1 flex items-center gap-3">
            <Badge status={shipment.currentStatus} />
            <div className="text-sm text-slate-500" data-testid="shipment-last-update">
              Updated {shipment.lastUpdate ? prettyDate(shipment.lastUpdate) : "—"}
            </div>
          </div>
        </div>
        <div className="grid gap-2 text-sm text-slate-700 md:text-right">
          <div data-testid="shipment-mode">Mode: <span className="font-semibold text-slate-900">{shipment.mode}</span></div>
          <div data-testid="shipment-type">Type: <span className="font-semibold text-slate-900">{shipment.shipmentType}</span></div>
          <div data-testid="shipment-eta">ETA: <span className="font-semibold text-slate-900">{shipment.eta ? prettyDate(shipment.eta) : "—"}</span></div>
        </div>
      </div>
      {shipment.currentStatus === "PACKAGE_ON_HOLD" && (
        <div className="mt-4 rounded-xl border border-orange-200 bg-orange-50 p-4" data-testid="package-on-hold-notice">
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0 mt-0.5">
              <svg className="h-5 w-5 text-orange-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            <div>
              <div className="text-sm font-semibold text-orange-800">Your package is currently on hold</div>
              <div className="mt-1 text-sm text-orange-700">For more information regarding the status of your shipment, please contact our support team at <a href="mailto:support@primeharborlogistics.group" className="font-semibold underline">support@primeharborlogistics.group</a> or <a href="mailto:primeharborlogistics@gmail.com" className="font-semibold underline" data-testid="hold-support-email-alt">primeharborlogistics@gmail.com</a>.</div>
            </div>
          </div>
        </div>
      )}
      <div className="mt-6 grid gap-4 md:grid-cols-2">
        <div className="rounded-xl bg-slate-50 p-4" data-testid="shipment-origin-card">
          <div className="text-xs font-bold text-slate-600">ORIGIN</div>
          <div className="mt-1 text-sm font-semibold text-slate-900">{shipment.originCity}</div>
          <div className="text-sm text-slate-600">{shipment.originCountryCode}</div>
        </div>
        <div className="rounded-xl bg-slate-50 p-4" data-testid="shipment-dest-card">
          <div className="text-xs font-bold text-slate-600">DESTINATION</div>
          <div className="mt-1 text-sm font-semibold text-slate-900">{shipment.destCity}</div>
          <div className="text-sm text-slate-600">{shipment.destCountryCode}</div>
        </div>
      </div>
    </div>
  );
}

function Timeline({ events }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm" data-testid="tracking-timeline">
      <div className="text-sm font-bold text-slate-900">Tracking Updates</div>
      <div className="mt-4 space-y-4">
        {events.length === 0 ? (
          <div className="text-sm text-slate-600" data-testid="tracking-empty">No tracking events yet.</div>
        ) : (
          events.map((e, idx) => (
            <div key={e.id} className="flex gap-4" data-testid={`tracking-event-${idx}`}>
              <div className="mt-1 flex flex-col items-center">
                <div className={clsx("h-3 w-3 rounded-full", idx === 0 ? "bg-harbor-700" : "bg-slate-300")} />
                {idx !== events.length - 1 ? <div className="mt-1 h-full w-px bg-slate-200" /> : null}
              </div>
              <div className="flex-1">
                <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
                  <div className="flex items-center gap-3">
                    <Badge status={e.status} />
                    <div className="text-sm font-semibold text-slate-900" data-testid={`tracking-event-location-${idx}`}>{e.location}</div>
                  </div>
                  <div className="text-sm text-slate-500" data-testid={`tracking-event-time-${idx}`}>{prettyDate(e.eventTime)}</div>
                </div>
                {e.notes ? (
                  <div className="mt-2 text-sm text-slate-600" data-testid={`tracking-event-notes-${idx}`}>{e.notes}</div>
                ) : null}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

function prettyDate(value) {
  try {
    const d = new Date(value);
    return format(d, "PPpp");
  } catch {
    return value;
  }
}

// Convert a UTC ISO string to a local datetime string for datetime-local inputs
function utcToLocalDatetimeString(isoString) {
  if (!isoString) return "";
  try {
    const date = new Date(isoString);
    // Get the local date/time components
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  } catch {
    return "";
  }
}


function ModalShell({ title, children, onClose, testId }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4" data-testid={testId}>
      <div className="w-full max-w-xl rounded-2xl border border-slate-200 bg-white p-6 shadow-xl">
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="text-xs font-semibold text-slate-500">Admin</div>
            <div className="text-lg font-extrabold text-slate-900">{title}</div>
          </div>
          <button
            type="button"
            className="rounded-lg px-2 py-1 text-sm font-semibold text-slate-600 hover:bg-slate-100"
            onClick={onClose}
            data-testid={`${testId}-close`}
          >
            Close
          </button>
        </div>
        <div className="mt-4">{children}</div>
      </div>
    </div>
  );
}

function ConfirmDeleteModal({ title, description, onCancel, onConfirm }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const run = async () => {
    setLoading(true);
    setError("");
    try {
      await onConfirm();
    } catch (e) {
      setError(e.message || "Delete failed");
      setLoading(false);
    }
  };

  return (
    <ModalShell title={title} onClose={onCancel} testId="confirm-delete-modal">
      <div className="text-sm text-slate-700">{description}</div>
      {error ? (
        <div className="mt-4 rounded-xl border border-rose-200 bg-rose-50 p-3 text-sm text-rose-800">{error}</div>
      ) : null}
      <div className="mt-6 flex justify-end gap-2">
        <SecondaryButton type="button" onClick={onCancel} disabled={loading} data-testid="confirm-delete-cancel">
          Cancel
        </SecondaryButton>
        <PrimaryButton
          type="button"
          onClick={run}
          disabled={loading}
          className="bg-rose-700 hover:bg-rose-600"
          data-testid="confirm-delete-confirm"
        >
          {loading ? "Deleting..." : "Delete"}
        </PrimaryButton>
      </div>
    </ModalShell>
  );
}

function ServiceLanding({ title, subtitle, bullets, image, testId }) {
  return (
    <div className="bg-white" data-testid={testId}>
      <div className="border-b border-slate-200 bg-gradient-to-b from-slate-50 to-white">
        <Container className="py-12">
          <div className="grid gap-8 md:grid-cols-[1fr_360px]">
            <div>
              <div className="text-xs font-semibold text-slate-500">Services</div>
              <h1 className="mt-2 text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-slate-900">{title}</h1>
              <p className="mt-4 text-base sm:text-sm text-slate-600 max-w-2xl">{subtitle}</p>
              <ul className="mt-6 space-y-2 text-sm text-slate-700">
                {(bullets || []).map((b, idx) => (
                  <li key={idx} className="flex gap-2">
                    <span className="mt-2 inline-block h-1.5 w-1.5 rounded-full bg-harbor-700" />
                    <span>{b}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-8 flex flex-wrap gap-3">
                <Link to="/quote">
                  <PrimaryButton>Request a Quote</PrimaryButton>
                </Link>
                <Link to="/track">
                  <SecondaryButton>Track a Shipment</SecondaryButton>
                </Link>
              </div>
            </div>
            <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
              {image ? <img src={image} alt={title} className="h-full w-full object-cover" loading="lazy" /> : null}
            </div>
          </div>
        </Container>
      </div>
    </div>
  );
}
// (moved above)

function ShipmentEditModal({ auth, shipment, onClose, onSaved }) {
  const [countries, setCountries] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const [mode, setMode] = useState(shipment.mode || "AIR");
  const [shipmentType, setShipmentType] = useState(shipment.shipmentType || "PARCEL");
  const [originCountryCode, setOriginCountryCode] = useState(shipment.originCountryCode || "US");
  const [originCity, setOriginCity] = useState(shipment.originCity || "");
  const [destCountryCode, setDestCountryCode] = useState(shipment.destCountryCode || "US");
  const [destCity, setDestCity] = useState(shipment.destCity || "");
  const [eta, setEta] = useState(utcToLocalDatetimeString(shipment.eta));
  const [carrier, setCarrier] = useState(shipment.carrier || "");
  const [referenceNumber, setReferenceNumber] = useState(shipment.referenceNumber || "");

  useEffect(() => {
    apiFetch("/api/countries").then((d) => setCountries(d.countries || [])).catch(() => setCountries([]));
  }, []);

  const save = async () => {
    setSaving(true);
    setError("");
    try {
      const payload = {
        mode,
        shipmentType,
        originCountryCode,
        originCity,
        destCountryCode,
        destCity,
        eta: eta ? new Date(eta).toISOString() : null,
        carrier: carrier || null,
        referenceNumber: referenceNumber || null,
      };
      await apiFetch(`/api/admin/shipments/${shipment.id}`, { method: "PATCH", token: auth.token, body: payload });
      await onSaved();
    } catch (e) {
      setError(e.message || "Save failed");
      setSaving(false);
    }
  };

  return (
    <ModalShell title={`Edit ${shipment.trackingId}`} onClose={onClose} testId="shipment-edit-modal">
      <div className="grid gap-4">
        <div className="grid gap-4 md:grid-cols-2">
          <Field label="Mode" required>
            <Select value={mode} onChange={(e) => setMode(e.target.value)} data-testid="edit-mode">
              <option value="AIR">Air</option>
              <option value="SEA">Sea</option>
              <option value="ROAD">Road</option>
            </Select>
          </Field>
          <Field label="Shipment type" required>
            <Select value={shipmentType} onChange={(e) => setShipmentType(e.target.value)} data-testid="edit-shipment-type">
              <option value="PARCEL">Parcel</option>
              <option value="FREIGHT">Freight</option>
            </Select>
          </Field>
        </div>

        <div className="grid gap-4 md:grid-cols-2">
          <Field label="Origin country" required>
            <Select value={originCountryCode} onChange={(e) => setOriginCountryCode(e.target.value)} data-testid="edit-origin-country">
              {(countries || []).map((c) => (
                <option key={c.code} value={c.code}>
                  {c.name} ({c.code})
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Origin city" required>
            <Input value={originCity} onChange={(e) => setOriginCity(e.target.value)} data-testid="edit-origin-city" />
          </Field>
        </div>

        <div className="grid gap-4 md:grid-cols-2">
          <Field label="Destination country" required>
            <Select value={destCountryCode} onChange={(e) => setDestCountryCode(e.target.value)} data-testid="edit-dest-country">
              {(countries || []).map((c) => (
                <option key={c.code} value={c.code}>
                  {c.name} ({c.code})
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Destination city" required>
            <Input value={destCity} onChange={(e) => setDestCity(e.target.value)} data-testid="edit-dest-city" />
          </Field>
        </div>

        <div className="grid gap-4 md:grid-cols-3">
          <Field label="ETA">
            <Input type="datetime-local" value={eta} onChange={(e) => setEta(e.target.value)} data-testid="edit-eta" />
          </Field>
          <Field label="Carrier">
            <Input value={carrier} onChange={(e) => setCarrier(e.target.value)} data-testid="edit-carrier" />
          </Field>
          <Field label="Reference number">
            <Input value={referenceNumber} onChange={(e) => setReferenceNumber(e.target.value)} data-testid="edit-reference" />
          </Field>
        </div>

        {error ? (
          <div className="rounded-xl border border-rose-200 bg-rose-50 p-3 text-sm text-rose-800" data-testid="edit-error">{error}</div>
        ) : null}

        <div className="flex justify-end gap-2">
          <SecondaryButton type="button" onClick={onClose} disabled={saving} data-testid="edit-cancel">
            Cancel
          </SecondaryButton>
          <PrimaryButton type="button" onClick={save} disabled={saving} data-testid="edit-save">
            {saving ? "Saving..." : "Save changes"}
          </PrimaryButton>
        </div>
      </div>
    </ModalShell>
  );
}

// modal helpers moved above

function ServicesPage() {
  return (
    <div data-testid="services-page">
      <div className="relative overflow-hidden bg-ink-900">
        <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${IMG_SEA})` }} aria-hidden="true" />
        <div className="absolute inset-0 bg-gradient-to-b from-ink-900/85 via-ink-900/75 to-ink-900/30" aria-hidden="true" />
        <Container className="relative py-14">
          <div className="max-w-3xl">
            <div className="text-xs font-semibold text-white/70" data-testid="services-hero-kicker">Services</div>
            <h2 className="mt-2 text-3xl font-extrabold tracking-tight text-white md:text-4xl" data-testid="services-title">
              Worldwide freight, managed end-to-end.
            </h2>
            <p className="mt-3 text-sm leading-relaxed text-white/80" data-testid="services-subtitle">
              PrimeHarbor provides multi-modal shipping with customs and warehousing support — designed for operations teams
              that need predictable milestones, clean documentation, and consistent communication.
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <Link to="/quote" data-testid="services-hero-quote-link"><PrimaryButton>Get a Quote <ArrowRightIcon className="h-4 w-4" /></PrimaryButton></Link>
              <Link to="/track" data-testid="services-hero-track-link"><SecondaryButton>Track a Shipment</SecondaryButton></Link>
            </div>
          </div>
        </Container>
      </div>

      <Container className="py-14">
        <div className="grid gap-8 md:grid-cols-2" data-testid="services-detail-grid">
          <ServiceDetail
            title="Air Freight"
            image={IMG_AIR}
            testId="services-detail-air"
            bullets={[
              "Priority uplift for time-critical shipments",
              "Airport handling + handover visibility",
              "Proactive updates across hubs",
            ]}
          />
          <ServiceDetail
            title="Sea Freight"
            image={IMG_SEA}
            testId="services-detail-sea"
            bullets={[
              "FCL/LCL across major ports",
              "Port milestones + customs coordination",
              "Carrier and reference tracking",
            ]}
          />
          <ServiceDetail
            title="Road Freight"
            image={IMG_WAREHOUSE}
            testId="services-detail-road"
            bullets={[
              "Regional linehaul and last-mile coordination",
              "Hub scans and exception handling",
              "Delivery milestones and proof points",
            ]}
          />
          <ServiceDetail
            title="Customs + Compliance"
            image={IMG_HERO}
            testId="services-detail-customs"
            bullets={[
              "Document checks and clearance support",
              "Incoterms-aware shipment workflows",
              "Audit-friendly tracking history",
            ]}
          />
        </div>

        <div className="mt-14 grid gap-6 rounded-2xl border border-slate-200 bg-slate-50 p-8 md:grid-cols-3" data-testid="services-trust">
          <TrustCard title="Operational visibility" desc="Latest status, last update, and location timeline for every shipment." testId="trust-card-visibility" />
          <TrustCard title="Global coverage" desc="Validated country selection and flexible city routing across lanes." testId="trust-card-coverage" />
          <TrustCard title="Document control" desc="Cloud-stored docs (invoice, B/L, packing list) attached to shipments." testId="trust-card-docs" />
        </div>

        <div className="mt-14 grid gap-10 md:grid-cols-2" data-testid="services-faq">
          <div>
            <div className="text-xs font-semibold text-slate-500">FAQ</div>
            <div className="mt-2 text-2xl font-extrabold text-slate-900" data-testid="services-faq-title">Common questions</div>
            <p className="mt-2 text-sm text-slate-600">Fast answers for procurement and operations teams.</p>
          </div>
          <div className="space-y-3">
            <FaqItem q="How quickly will I receive a quote?" a="Most quote requests are responded to within one business day. Urgent lanes can be prioritized if you include a deadline in the request." testId="faq-item-1" />
            <FaqItem q="What information do you need to provide pricing?" a="Origin/destination, mode preference, shipment type, dimensions/weight, cargo description, and ready/pickup date. Incoterms and special handling requirements help refine the estimate." testId="faq-item-2" />
            <FaqItem q="How does shipment tracking work?" a="Once a shipment is created, you receive a tracking ID. Milestones (pickup, hub, customs, delivery) are posted to a timeline so stakeholders can monitor progress without a login." testId="faq-item-3" />
          </div>
        </div>

        <div className="mt-14 rounded-2xl border border-slate-200 bg-white p-8 shadow-sm" data-testid="services-cta">
          <div className="grid gap-6 md:grid-cols-2 md:items-center">
            <div>
              <div className="text-xs font-semibold text-slate-500">Ready to start?</div>
              <div className="mt-2 text-2xl font-extrabold text-slate-900">Get a tailored quote for your next lane.</div>
              <p className="mt-2 text-sm text-slate-600">
                Share origin, destination, and cargo details. We’ll respond with pricing and recommended mode.
              </p>
            </div>
            <div className="flex flex-wrap justify-start gap-3 md:justify-end">
              <Link to="/quote" data-testid="services-cta-quote-link"><PrimaryButton>Get a Quote <ArrowRightIcon className="h-4 w-4" /></PrimaryButton></Link>
              <Link to="/track" data-testid="services-cta-track-link"><SecondaryButton>Track Shipment</SecondaryButton></Link>
            </div>
          </div>
        </div>
      </Container>
    </div>
  );
}

// InfoPanel removed (no longer used)

function QuotePage() {
  const [countries, setCountries] = useState([]);
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    shipmentType: "PARCEL",
    mode: "AIR",
    originCountryCode: "US",
    originCity: "",
    destCountryCode: "GB",
    destCity: "",
    weightKg: "",
    dimensions: "",
    cargoDescription: "",
    incoterms: "",
    pickupDate: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [successId, setSuccessId] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    let mounted = true;
    apiFetch("/api/countries")
      .then((d) => mounted && setCountries(d.countries || []))
      .catch(() => mounted && setCountries([]));
    return () => {
      mounted = false;
    };
  }, []);

  const onChange = (key) => (e) => {
    setForm((f) => ({ ...f, [key]: e.target.value }));
    setError("");
  };

  const submit = async () => {
    setSubmitting(true);
    setError("");
    try {
      const weight = Number(form.weightKg);
      if (Number.isNaN(weight) || weight <= 0) {
        throw new Error("Weight must be a valid number in kg (e.g., 150). Remove letters like 'k'.");
      }

      const payload = {
        ...form,
        weightKg: weight,
        pickupDate: form.pickupDate ? new Date(form.pickupDate).toISOString() : null,
      };
      const resp = await apiFetch("/api/quote", { method: "POST", body: payload });
      setSuccessId(resp.id);
    } catch (e) {
      setError(e.message || "Failed to submit");
    } finally {
      setSubmitting(false);
    }
  };

  if (successId) {
    return (
      <Container className="py-10" data-testid="quote-success-page">
        <div className="max-w-2xl rounded-2xl border border-emerald-200 bg-emerald-50 p-8">
          <div className="text-sm font-bold text-emerald-900" data-testid="quote-success-title">Quote request received</div>
          <div className="mt-2 text-sm text-emerald-800" data-testid="quote-success-message">
            Thanks — our team will reach out soon. Your request ID is <span className="font-mono font-semibold">{successId}</span>.
          </div>
          <div className="mt-6 flex gap-3">
            <Link to="/" data-testid="quote-success-home-link"><SecondaryButton>Back to home</SecondaryButton></Link>
            <Link to="/track" data-testid="quote-success-track-link"><PrimaryButton>Track shipment</PrimaryButton></Link>
          </div>
        </div>
      </Container>
    );
  }

  return (
    <div data-testid="quote-page">
      <div className="relative overflow-hidden bg-ink-900">
        <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${IMG_AIR})` }} aria-hidden="true" />
        <div className="absolute inset-0 bg-gradient-to-b from-ink-900/85 via-ink-900/75 to-ink-900/30" aria-hidden="true" />
        <Container className="relative py-14">
          <div className="max-w-3xl">
            <div className="text-xs font-semibold text-white/70" data-testid="quote-hero-kicker">Get a Quote</div>
            <h2 className="mt-2 text-3xl font-extrabold tracking-tight text-white md:text-4xl" data-testid="quote-title">Request pricing for your shipment</h2>
            <p className="mt-3 text-sm leading-relaxed text-white/80" data-testid="quote-hero-subtitle">
              Share your route and cargo details. Our team will respond with recommended mode, documentation requirements, and pricing.
            </p>
          </div>
        </Container>
      </div>

      <Container className="py-14">
        <div className="grid gap-8 md:grid-cols-[1fr_340px]" data-testid="quote-layout">
          <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm" data-testid="quote-form-card">
            <div className="text-sm font-extrabold text-slate-900">Shipment details</div>
            <div className="mt-1 text-sm text-slate-600">We typically respond within 2 business hours.</div>

            <div className="mt-6 grid gap-4">
              <div className="grid gap-4 md:grid-cols-2" data-testid="quote-contact-grid">
                <Field label="Full name" required>
                  <Input data-testid="quote-name-input" value={form.name} onChange={onChange("name")} placeholder="Jane Doe" />
                </Field>
                <Field label="Email" required>
                  <Input data-testid="quote-email-input" value={form.email} onChange={onChange("email")} placeholder="jane@company.com" />
                </Field>
                <Field label="Phone">
                  <Input data-testid="quote-phone-input" value={form.phone} onChange={onChange("phone")} placeholder="+1 ..." />
                </Field>
                <Field label="Company">
                  <Input data-testid="quote-company-input" value={form.company} onChange={onChange("company")} placeholder="Optional" />
                </Field>
              </div>

              <div className="grid gap-4 md:grid-cols-3">
                <Field label="Shipment type" required>
              <Select data-testid="quote-shipment-type-select" value={form.shipmentType} onChange={onChange("shipmentType")}>
                <option value="PARCEL">Parcel</option>
                <option value="FREIGHT">Freight</option>
              </Select>
            </Field>
                <Field label="Mode" required>
                  <Select data-testid="quote-mode-select" value={form.mode} onChange={onChange("mode")}>
                <option value="AIR">Air</option>
                <option value="SEA">Sea</option>
                <option value="ROAD">Road</option>
              </Select>
                </Field>
                <Field label="Weight (kg)" required>
                  <Input
                data-testid="quote-weight-input"
                value={form.weightKg}
                onChange={onChange("weightKg")}
                placeholder="e.g., 125"
                inputMode="decimal"
              />
                </Field>
              </div>

              <div className="grid gap-4 md:grid-cols-2">
            <Field label="Origin country" required>
              <Select data-testid="quote-origin-country-select" value={form.originCountryCode} onChange={onChange("originCountryCode")}>
                {countries.map((c) => (
                  <option key={c.code} value={c.code}>
                    {c.name} ({c.code})
                  </option>
                ))}
              </Select>
            </Field>
            <Field label="Origin city" required>
              <Input data-testid="quote-origin-city-input" value={form.originCity} onChange={onChange("originCity")} placeholder="City" />
            </Field>
            <Field label="Destination country" required>
              <Select data-testid="quote-dest-country-select" value={form.destCountryCode} onChange={onChange("destCountryCode")}>
                {countries.map((c) => (
                  <option key={c.code} value={c.code}>
                    {c.name} ({c.code})
                  </option>
                ))}
              </Select>
            </Field>
            <Field label="Destination city" required>
              <Input data-testid="quote-dest-city-input" value={form.destCity} onChange={onChange("destCity")} placeholder="City" />
            </Field>
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <Field label="Dimensions">
              <Input data-testid="quote-dimensions-input" value={form.dimensions} onChange={onChange("dimensions")} placeholder="L×W×H (optional)" />
            </Field>
            <Field label="Desired pickup date">
              <Input data-testid="quote-pickup-date-input" value={form.pickupDate} onChange={onChange("pickupDate")} type="date" />
            </Field>
          </div>

          <Field label="Cargo description" required>
            <TextArea
              data-testid="quote-cargo-description-input"
              value={form.cargoDescription}
              onChange={onChange("cargoDescription")}
              rows={4}
              placeholder="What are you shipping?"
            />
          </Field>

          <div className="grid gap-4 md:grid-cols-2">
            <Field label="Incoterms">
              <Input data-testid="quote-incoterms-input" value={form.incoterms} onChange={onChange("incoterms")} placeholder="DAP, FOB, CIF (optional)" />
            </Field>
          </div>

          {error ? (
            <div className="rounded-xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-800" data-testid="quote-error">
              {error}
            </div>
          ) : null}

          <div className="flex flex-wrap gap-3">
            <PrimaryButton onClick={submit} disabled={submitting} data-testid="quote-submit-button">
              {submitting ? "Submitting..." : "Submit request"} <PaperAirplaneIcon className="h-4 w-4" />
            </PrimaryButton>
            <Link to="/services" data-testid="quote-services-link"><SecondaryButton type="button">View services</SecondaryButton></Link>
          </div>

            {/* disclaimer removed */}
          </div>

          </div>


          <div className="space-y-4" data-testid="quote-side">
            <div className="rounded-2xl border border-slate-200 bg-slate-50 p-6" data-testid="quote-side-what-happens">
              <div className="text-sm font-extrabold text-slate-900">What happens next</div>
              <ol className="mt-3 space-y-2 text-sm text-slate-700">
                <li data-testid="quote-next-1">1) We validate lane + pickup requirements</li>
                <li data-testid="quote-next-2">2) We recommend Air/Sea/Road based on cost vs speed</li>
                <li data-testid="quote-next-3">3) You receive a quote and proposed timeline</li>
              </ol>
            </div>

            <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm" data-testid="quote-side-image">
              <div className="relative h-44">
                <img src={IMG_WAREHOUSE} alt="Warehouse operations" className="h-full w-full object-cover" loading="lazy" />
                <div className="absolute inset-0 bg-gradient-to-t from-ink-900/50 to-transparent" />
              </div>
              <div className="p-6">
                <div className="text-sm font-extrabold text-slate-900">Operations-ready</div>
                <div className="mt-2 text-sm text-slate-600">
                  Quote requests are stored and visible in the admin dashboard. Shipments then receive tracking IDs and timeline updates.
                </div>
              </div>
            </div>

            <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm" data-testid="quote-side-cta">
              <div className="text-xs font-semibold text-slate-500">Already have a tracking ID?</div>
              <div className="mt-2 text-sm font-extrabold text-slate-900">Track your shipment</div>
              <div className="mt-4">
                <Link to="/track" data-testid="quote-side-track-link"><SecondaryButton className="w-full">Track Shipment</SecondaryButton></Link>
              </div>
            </div>
          </div>
        </div>
      </Container>
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <label className="grid gap-1" data-testid={`field-${label.toLowerCase().replaceAll(" ", "-")}`}>
      <div className="flex items-center gap-2 text-sm font-semibold text-slate-900">
        {label} {required ? <span className="text-rose-600">*</span> : null}
      </div>
      {children}
    </label>
  );
}

// SimplePage removed (no longer used)

function AboutPage() {
  return (
    <div data-testid="about-page">
      <div className="relative overflow-hidden bg-ink-900">
        <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${IMG_HERO})` }} aria-hidden="true" />
        <div className="absolute inset-0 bg-gradient-to-b from-ink-900/85 via-ink-900/75 to-ink-900/30" aria-hidden="true" />
        <Container className="relative py-14">
          <div className="max-w-3xl">
            <div className="text-xs font-semibold text-white/70" data-testid="about-hero-kicker">About</div>
            <h2 className="mt-2 text-3xl font-extrabold tracking-tight text-white md:text-4xl" data-testid="about-page-title">
              Built for global shipping operations.
            </h2>
            <p className="mt-3 text-sm leading-relaxed text-white/80" data-testid="about-page-subtitle">
              PrimeHarbor Logistics supports international air, sea, and road freight with customs coordination and warehouse
              operations — all backed by an enterprise tracking timeline.
            </p>
          </div>
        </Container>
      </div>

      <Container className="py-14">
        <div className="grid gap-8 md:grid-cols-2" data-testid="about-content-grid">
          <div className="rounded-2xl border border-slate-200 bg-white p-8 shadow-sm" data-testid="about-mission">
            <div className="text-xs font-semibold text-slate-500">Mission</div>
            <div className="mt-2 text-2xl font-extrabold text-slate-900">Move freight with clarity.</div>
            <p className="mt-3 text-sm text-slate-600">
              We help teams reduce uncertainty with structured milestones, consistent updates, and documentation that travels with the shipment.
            </p>
          </div>
          <div className="rounded-2xl border border-slate-200 bg-slate-50 p-8" data-testid="about-why">
            <div className="text-xs font-semibold text-slate-500">Why PrimeHarbor</div>
            <ul className="mt-4 space-y-2 text-sm text-slate-700" data-testid="about-why-bullets">
              <li data-testid="about-why-1">• Multi-modal freight coordination (air/sea/road)</li>
              <li data-testid="about-why-2">• Customs paperwork and compliance support</li>
              <li data-testid="about-why-3">• Cloud document handling (invoice, B/L, packing list)</li>
              <li data-testid="about-why-4">• Public tracking links — no login required</li>
            </ul>
          </div>
        </div>

        <div className="mt-14 rounded-2xl border border-slate-200 bg-white p-8 shadow-sm" data-testid="about-stats">
          <div className="grid gap-6 md:grid-cols-3">
            <Stat label="Countries served" value="200+" testId="about-stat-countries" />
            <Stat label="Active lanes" value="120+" testId="about-stat-lanes" />
            <Stat label="Operational support" value="24/7" testId="about-stat-support" />
          </div>
        </div>
      </Container>
    </div>
  );
}

function AirFreightPage() {
  return (
    <ServiceLanding
      title="Air Freight"
      subtitle="Fast, reliable uplift with proactive milestone tracking and exception handling."
      bullets={["Airport-to-door options", "Priority handling + reliable ETAs", "Visibility: pickup → hub → destination", "Document management for compliance"]}
      image={IMG_AIR}
      testId="service-air-freight-page"
    />
  );
}

function SeaFreightPage() {
  return (
    <ServiceLanding
      title="Sea Freight"
      subtitle="Cost-efficient ocean shipping for FCL/LCL lanes with port handling and milestone visibility."
      bullets={["FCL and LCL shipments", "Port + customs coordination", "Milestone tracking across legs", "Secure document uploads"]}
      image={IMG_SEA}
      testId="service-sea-freight-page"
    />
  );
}

function RoadFreightPage() {
  return (
    <ServiceLanding
      title="Road Freight"
      subtitle="Regional linehaul and last-mile coordination with clear delivery milestones."
      bullets={["Domestic + cross-border road movement", "Hub transfers and last mile", "Delivery confirmation workflows", "Exception tracking"]}
      image={IMG_HERO}
      testId="service-road-freight-page"
    />
  );
}

function CustomsPage() {
  return (
    <ServiceLanding
      title="Customs"
      subtitle="Documentation, compliance checks, and broker coordination to keep cargo moving."
      bullets={["Commercial invoice + packing list handling", "Compliance review and escalation", "Clear milestones + audit trail", "Centralized documents per shipment"]}
      image={IMG_WAREHOUSE}
      testId="service-customs-page"
    />
  );
}

function WarehousingPage() {
  return (
    <ServiceLanding
      title="Warehousing"
      subtitle="Secure storage, consolidation, and dispatch operations with shipment-level visibility."
      bullets={["Inbound receiving + storage", "Consolidation and dispatch", "Inventory-friendly documentation", "Operational visibility for milestones"]}
      image={IMG_WAREHOUSE}
      testId="service-warehousing-page"
    />
  );
}

// SecureAdminPage removed


function ContactPage() {
  return (
    <div data-testid="contact-page">
      <div className="relative overflow-hidden bg-ink-900">
        <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${IMG_WAREHOUSE})` }} aria-hidden="true" />
        <div className="absolute inset-0 bg-gradient-to-b from-ink-900/85 via-ink-900/75 to-ink-900/30" aria-hidden="true" />
        <Container className="relative py-14">
          <div className="max-w-3xl">
            <div className="text-xs font-semibold text-white/70" data-testid="contact-hero-kicker">Contact</div>
            <h2 className="mt-2 text-3xl font-extrabold tracking-tight text-white md:text-4xl" data-testid="contact-page-title">
              Talk to PrimeHarbor.
            </h2>
            <p className="mt-3 text-sm leading-relaxed text-white/80" data-testid="contact-page-subtitle">
              Reach us for quote follow-ups, shipment exceptions, or documentation support.
            </p>
          </div>
        </Container>
      </div>

      <Container className="py-14">
        <div className="grid gap-8 md:grid-cols-2" data-testid="contact-grid">
          <div className="rounded-2xl border border-slate-200 bg-white p-8 shadow-sm" data-testid="contact-card">
            <div className="text-sm font-extrabold text-slate-900">Support</div>
            <div className="mt-4 space-y-2 text-sm text-slate-700">
              <div data-testid="contact-email">Email: <span className="font-semibold">support@primeharborlogistics.group</span></div>
              <div data-testid="contact-email-alt">Email: <span className="font-semibold">primeharborlogistics@gmail.com</span></div>

              <div data-testid="contact-hours">Hours: <span className="font-semibold">24/7 for in-transit exceptions</span></div>
            </div>
            <div className="mt-6 rounded-xl bg-slate-50 p-4">
              <div className="text-xs font-semibold text-slate-500">For fastest response</div>
              <div className="mt-2 text-sm text-slate-700">Include your tracking ID in the email subject line.</div>
            </div>
          </div>
          <div className="rounded-2xl border border-slate-200 bg-slate-50 p-8" data-testid="contact-cta">
            <div className="text-xs font-semibold text-slate-500">Need pricing?</div>
            <div className="mt-2 text-2xl font-extrabold text-slate-900">Request a quote</div>
            <p className="mt-2 text-sm text-slate-600">Share route and cargo details and we’ll respond with recommended mode and pricing.</p>
            <div className="mt-6">
              <Link to="/quote" data-testid="contact-quote-link"><PrimaryButton>Get a Quote <ArrowRightIcon className="h-4 w-4" /></PrimaryButton></Link>
            </div>
          </div>
        </div>
      </Container>
    </div>
  );
}

function PrivacyPage() {
  return (
    <div data-testid="privacy-page">
      <div className="relative overflow-hidden bg-ink-900">
        <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${IMG_SEA})` }} aria-hidden="true" />
        <div className="absolute inset-0 bg-gradient-to-b from-ink-900/85 via-ink-900/75 to-ink-900/30" aria-hidden="true" />
        <Container className="relative py-14">
          <div className="max-w-3xl">
            <div className="text-xs font-semibold text-white/70" data-testid="privacy-hero-kicker">Legal</div>
            <h2 className="mt-2 text-3xl font-extrabold tracking-tight text-white md:text-4xl" data-testid="privacy-page-title">
              Privacy Policy
            </h2>
            <p className="mt-3 text-sm leading-relaxed text-white/80" data-testid="privacy-page-subtitle">
              We collect only what’s required to process quote requests, manage shipments, and provide tracking.
            </p>
          </div>
        </Container>
      </div>

      <Container className="py-14">
        <div className="rounded-2xl border border-slate-200 bg-white p-8 shadow-sm" data-testid="privacy-content">
          <div className="prose prose-slate max-w-none text-sm">
            <p data-testid="privacy-p1">We store customer-provided data (name, email, route details) to respond to quote requests and manage shipments.</p>
            <p data-testid="privacy-p2">Tracking pages are public and can be accessed by anyone with a tracking ID.</p>
            <p data-testid="privacy-p3">Documents uploaded by staff are stored in Cloudinary and linked to shipments for operational purposes.</p>
          </div>
        </div>
      </Container>
    </div>
  );
}

function TermsPage() {
  return (
    <div data-testid="terms-page">
      <div className="relative overflow-hidden bg-ink-900">
        <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${IMG_AIR})` }} aria-hidden="true" />
        <div className="absolute inset-0 bg-gradient-to-b from-ink-900/85 via-ink-900/75 to-ink-900/30" aria-hidden="true" />
        <Container className="relative py-14">
          <div className="max-w-3xl">
            <div className="text-xs font-semibold text-white/70" data-testid="terms-hero-kicker">Legal</div>
            <h2 className="mt-2 text-3xl font-extrabold tracking-tight text-white md:text-4xl" data-testid="terms-page-title">
              Terms
            </h2>
            <p className="mt-3 text-sm leading-relaxed text-white/80" data-testid="terms-page-subtitle">
              MVP terms for evaluation and demonstration purposes.
            </p>
          </div>
        </Container>
      </div>

      <Container className="py-14">
        <div className="rounded-2xl border border-slate-200 bg-white p-8 shadow-sm" data-testid="terms-content">
          <div className="prose prose-slate max-w-none text-sm">
            <p data-testid="terms-p1">This service is provided as-is for MVP evaluation. No guarantees are made regarding transit times or delivery outcomes.</p>
            <p data-testid="terms-p2">For production, formal SLAs, liability limits, and compliance policies will be defined.</p>
          </div>
        </div>
      </Container>
    </div>
  );
}

// -----------------
// Admin area
// -----------------

function RequireAuth({ auth, children }) {
  const location = useLocation();
  if (!auth.isAuthed) {
    return <AdminLoginPage auth={auth} redirectTo={location.pathname} />;
  }
  return children;
}

function AdminLayout({ auth, children, topbar }) {
  return (
    <div className="min-h-[calc(100vh-64px)] bg-slate-50" data-testid="admin-layout">
      <div className="mx-auto w-full max-w-7xl px-4 py-8">
        <div className="grid gap-6 lg:grid-cols-[280px_1fr]">
          <div className="lg:sticky lg:top-6 lg:self-start">
            <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm" data-testid="admin-sidebar">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="text-xs font-semibold text-slate-500">ADMIN</div>
                  <div className="mt-1 text-sm font-bold text-slate-900" data-testid="admin-email">{auth.adminEmail}</div>
                </div>
                {/* brand pill removed */}
              </div>

              <div className="mt-5 space-y-2">
                <SideLink to="/admin" label="Dashboard" testId="admin-nav-dashboard" />
                <SideLink to="/admin/shipments" label="Shipments" testId="admin-nav-shipments" />
                <SideLink to="/admin/shipments/new" label="Create Shipment" testId="admin-nav-create-shipment" />
                <SideLink to="/admin/quotes" label="Quote Requests" testId="admin-nav-quotes" />
                <SideLink to="/admin/customers" label="Customers" testId="admin-nav-customers" />
                <SideLink to="/admin/settings" label="Settings" testId="admin-nav-settings" />
              </div>

              <div className="mt-6 border-t border-slate-100 pt-4">
                <SecondaryButton onClick={auth.logout} className="w-full" data-testid="admin-sidebar-logout-button">
                  Logout
                </SecondaryButton>
              </div>
            </div>
          </div>

          <div className="min-w-0">
            {topbar ? (
              <div className="mb-6 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm" data-testid="admin-topbar">
                {topbar}
              </div>
            ) : null}
            <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">{children}</div>
          </div>
        </div>
      </div>
    </div>
  );
}

function SideLink({ to, label, testId }) {
  return (
    <NavLink
      to={to}
      className={({ isActive }) =>
        clsx(
          "block rounded-xl px-4 py-3 text-sm font-semibold transition",
          isActive ? "bg-harbor-50 text-harbor-900 ring-1 ring-harbor-600/10" : "text-slate-700 hover:bg-slate-50"
        )
      }
      data-testid={testId}
    >
      {label}
    </NavLink>
  );
}

function AdminLoginPage({ auth, redirectTo = "/admin" }) {
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const sessionExpired = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return params.get("session") === "expired";
  }, [location.search]);

  useEffect(() => {
    // In case we were redirected here due to token invalidation, ensure form is usable.
    setLoading(false);
  }, [sessionExpired]);

  const navigate = useNavigate();

  const submit = async () => {
    setLoading(true);
    setError("");
    try {
      await auth.login(email, password);
      navigate(redirectTo);
    } catch (e) {
      setError(e.message || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container className="py-12" data-testid="admin-login-page">
      <div className="mx-auto max-w-md rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
        <div className="flex items-center gap-3">
          <div className="grid h-10 w-10 place-items-center rounded-xl bg-ink-900 text-white">
            <LockClosedIcon className="h-5 w-5" />
          </div>
          <div>
            <div className="text-lg font-extrabold text-slate-900" data-testid="admin-login-title">Admin Login</div>
            <div className="text-sm text-slate-600">Use the seeded admin credentials.</div>
          </div>
        </div>

        <div className="mt-6 grid gap-4">
          {sessionExpired ? (
            <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900" data-testid="admin-session-expired">
              Your session expired after the latest update. Please sign in again.
            </div>
          ) : null}
          <Field label="Email" required>
            <Input data-testid="admin-login-email-input" value={email} onChange={(e) => setEmail(e.target.value)} />
          </Field>
          <Field label="Password" required>
            <Input
              data-testid="admin-login-password-input"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </Field>
          {error ? (
            <div className="rounded-xl border border-rose-200 bg-rose-50 p-3 text-sm text-rose-800" data-testid="admin-login-error">
              {error}
            </div>
          ) : null}
          <PrimaryButton onClick={submit} disabled={loading} data-testid="admin-login-submit-button">
            {loading ? "Signing in..." : "Sign in"}
          </PrimaryButton>
          {!BACKEND_URL ? (
            <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800" data-testid="missing-backend-url-warning">
              REACT_APP_BACKEND_URL is not set. Add it in /app/frontend/.env (it must match the platform-provided backend URL).
            </div>
          ) : null}
        </div>
      </div>
    </Container>
  );
}

function AdminDashboard({ auth }) {
  const [kpis, setKpis] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let mounted = true;
    apiFetch("/api/admin/kpis", { token: auth.token })
      .then((d) => mounted && setKpis(d))
      .catch((e) => mounted && setError(e.message || "Failed"))
      .finally(() => mounted && setLoading(false));
    return () => {
      mounted = false;
    };
  }, [auth.token]);

  return (
    <AdminLayout auth={auth}>
      <div data-testid="admin-dashboard">
        <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
          <div>
            <div className="text-xs font-semibold text-slate-500">Dashboard</div>
            <div className="text-2xl font-extrabold text-slate-900" data-testid="admin-dashboard-title">Operations Overview</div>
          </div>
          <div className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-600" data-testid="admin-dashboard-hint">
            Tip: Use Shipments → Add Event to update the public timeline.
          </div>
        </div>

        {loading ? (
          <div className="mt-6 rounded-2xl border border-slate-200 bg-white p-6" data-testid="admin-kpis-loading">
            <div className="h-4 w-52 animate-pulse rounded bg-slate-100" />
          </div>
        ) : error ? (
          <div className="mt-6 rounded-2xl border border-rose-200 bg-rose-50 p-6 text-sm text-rose-800" data-testid="admin-kpis-error">
            {error}
          </div>
        ) : (
          <>
            <div className="mt-6 grid gap-4 md:grid-cols-4" data-testid="admin-kpis-grid">
              <Kpi label="Total shipments" value={kpis.totalShipments} testId="kpi-total-shipments" />
              <Kpi label="In transit" value={kpis.shipmentsInTransit} testId="kpi-in-transit" />
              <Kpi label="Delivered last 7 days" value={kpis.deliveredLast7Days} testId="kpi-delivered-7" />
              <Kpi label="Open quote requests" value={kpis.openQuoteRequests} testId="kpi-open-quotes" />
            </div>
            <div className="mt-6 rounded-2xl border border-slate-200 bg-white p-6" data-testid="admin-recent-activity">
              <div className="text-sm font-bold text-slate-900">Recent activity</div>
              <div className="mt-4 space-y-3">
                {(kpis.recentActivity || []).length === 0 ? (
                  <div className="text-sm text-slate-600" data-testid="admin-recent-empty">No activity yet.</div>
                ) : (
                  (kpis.recentActivity || []).map((e, idx) => (
                    <div key={e.id} className="flex items-center justify-between gap-3 rounded-xl bg-slate-50 px-4 py-3" data-testid={`admin-recent-${idx}`}>
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <Badge status={e.status} />
                          <div className="truncate text-sm font-semibold text-slate-900">{e.location}</div>
                        </div>
                        <div className="mt-1 text-xs text-slate-500">Shipment: {e.shipmentId}</div>
                      </div>
                      <div className="text-xs text-slate-500">{prettyDate(e.eventTime)}</div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </AdminLayout>
  );
}

function Kpi({ label, value, testId }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm" data-testid={testId}>
      <div className="text-xs font-semibold text-slate-500">{label}</div>
      <div className="mt-2 text-2xl font-extrabold text-slate-900" data-testid={`${testId}-value`}>{value ?? "—"}</div>
    </div>
  );
}

function AdminShipmentsList({ auth }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [q, setQ] = useState("");
  const [mode, setMode] = useState("");
  const [status, setStatus] = useState("");

  const [searchText, setSearchText] = useState("");
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const qs = new URLSearchParams();
      if (q) qs.set("q", q);
      if (mode) qs.set("mode", mode);
      if (status) qs.set("status", status);
      const data = await apiFetch(`/api/admin/shipments?${qs.toString()}`, { token: auth.token });
      setItems(data.items || []);
    } catch (e) {
      setError(e.message || "Failed");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <AdminLayout
      auth={auth}
      topbar={
        <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <div>
            <div className="text-xs font-semibold text-slate-500">Search</div>
            <div className="text-sm font-bold text-slate-900">Find by Tracking ID</div>
          </div>

          <div className="flex w-full max-w-xl items-center gap-2">
            <Input
              data-testid="admin-topbar-search-input"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  setQ(searchText);
                  setTimeout(() => load(), 0);
                }
              }}
              placeholder="Search (e.g., PHL-2026-XXXXXX)"
            />
            {searchText ? (
              <IconButton
                type="button"
                onClick={() => {
                  setSearchText("");
                  setQ("");
                  setTimeout(() => load(), 0);
                }}
                data-testid="admin-topbar-search-clear"
              >
                Clear
              </IconButton>
            ) : null}
            <PrimaryButton
              type="button"
              onClick={() => {
                setQ(searchText);
                load();
              }}
              data-testid="admin-topbar-search-button"
            >
              Search
            </PrimaryButton>
          </div>
        </div>
      }
    >
      <div data-testid="admin-shipments-page">
        <div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
          <div>
            <div className="text-xs font-semibold text-slate-500">Shipments</div>
            <div className="text-2xl font-extrabold text-slate-900" data-testid="admin-shipments-title">Manage shipments</div>
          </div>
          <div className="flex flex-wrap gap-2">
            <Link to="/admin/shipments/new" data-testid="admin-shipments-create-link"><PrimaryButton>Create shipment</PrimaryButton></Link>
          </div>
        </div>

        <div className="mt-5 grid gap-3 rounded-2xl border border-slate-200 bg-white p-4" data-testid="admin-shipments-filters">
          <div className="grid gap-3 md:grid-cols-4">
            <div>
              <div className="text-xs font-semibold text-slate-500">Search tracking ID</div>
              <Input data-testid="admin-shipments-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="PHL-YYYY-XXXXXX" />
            </div>
            <div>
              <div className="text-xs font-semibold text-slate-500">Mode</div>
              <Select data-testid="admin-shipments-mode-select" value={mode} onChange={(e) => setMode(e.target.value)}>
                <option value="">All</option>
                <option value="AIR">Air</option>
                <option value="SEA">Sea</option>
                <option value="ROAD">Road</option>
              </Select>
            </div>
            <div>
              <div className="text-xs font-semibold text-slate-500">Status</div>
              <Select data-testid="admin-shipments-status-select" value={status} onChange={(e) => setStatus(e.target.value)}>
                <option value="">All</option>
                <option value="LABEL_CREATED">Label Created</option>
                <option value="PICKED_UP">Picked Up</option>
                <option value="IN_TRANSIT">In Transit</option>
                <option value="ARRIVED_AT_HUB">Arrived at Hub</option>
                <option value="CUSTOMS">Customs</option>
                <option value="OUT_FOR_DELIVERY">Out for Delivery</option>
                <option value="DELIVERED">Delivered</option>
                <option value="EXCEPTION">Exception</option>
                <option value="PACKAGE_ON_HOLD">Package on Hold</option>
              </Select>
            </div>
            <div className="flex items-end">
              <PrimaryButton onClick={load} data-testid="admin-shipments-apply-filters-button">Apply</PrimaryButton>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="mt-6 rounded-2xl border border-slate-200 bg-white p-6" data-testid="admin-shipments-loading">
            <div className="h-4 w-64 animate-pulse rounded bg-slate-100" />
          </div>
        ) : error ? (
          <div className="mt-6 rounded-2xl border border-rose-200 bg-rose-50 p-6 text-sm text-rose-800" data-testid="admin-shipments-error">
            {error}
          </div>
        ) : (
          <div className="mt-6 overflow-hidden rounded-2xl border border-slate-200 bg-white" data-testid="admin-shipments-table">
            <div className="grid grid-cols-12 gap-2 border-b border-slate-200 bg-slate-50 px-4 py-3 text-xs font-bold text-slate-600">
              <div className="col-span-3">Tracking ID</div>
              <div className="col-span-3">Route</div>
              <div className="col-span-2">Mode</div>
              <div className="col-span-2">Status</div>
              <div className="col-span-2 text-right">Actions</div>
            </div>
            {items.length === 0 ? (
              <div className="px-4 py-6 text-sm text-slate-600" data-testid="admin-shipments-empty">No shipments.</div>
            ) : (
              items.map((s, idx) => (
                <div key={s.id} className="grid grid-cols-12 gap-2 border-b border-slate-100 px-4 py-3 text-sm" data-testid={`admin-shipments-row-${idx}`}>
                  <div className="col-span-3 font-mono font-semibold text-slate-900" data-testid={`admin-shipments-tracking-${idx}`}>{s.trackingId}</div>
                  <div className="col-span-3 text-slate-700">
                    {s.originCity} ({s.originCountryCode}) → {s.destCity} ({s.destCountryCode})
                  </div>
                  <div className="col-span-2 text-slate-700">{s.mode}</div>
                  <div className="col-span-2"><Badge status={s.currentStatus} /></div>
                  <div className="col-span-2 flex justify-end gap-2">
                    <Link to={`/admin/shipments/${s.id}`} data-testid={`admin-shipments-view-${idx}`}>
                      <SecondaryButton>View</SecondaryButton>
                    </Link>
                    <SecondaryButton onClick={() => setEditing(s)} data-testid={`admin-shipments-edit-${idx}`}>
                      Edit
                    </SecondaryButton>
                    <SecondaryButton onClick={() => setDeleting(s)} className="border-rose-200 text-rose-700 hover:bg-rose-50" data-testid={`admin-shipments-delete-${idx}`}>
                      Delete
                    </SecondaryButton>
                  </div>
                </div>
              ))
            )}
          </div>
        )}
      </div>
      {editing ? (
        <ShipmentEditModal
          auth={auth}
          shipment={editing}
          onClose={() => setEditing(null)}
          onSaved={async () => {
            setEditing(null);
            await load();
          }}
        />
      ) : null}

      {deleting ? (
        <ConfirmDeleteModal
          title="Delete shipment"
          description={`This will remove ${deleting.trackingId} from active tracking (soft-delete).`}
          onCancel={() => setDeleting(null)}
          onConfirm={async () => {
            await apiFetch(`/api/admin/shipments/${deleting.id}`, { method: "DELETE", token: auth.token });
            setDeleting(null);
            await load();
          }}
        />
      ) : null}
    </AdminLayout>
  );
}

function AdminCreateShipment({ auth }) {
  const [customers, setCustomers] = useState([]);
  const [countries, setCountries] = useState([]);
  const [mode, setMode] = useState("AIR");
  const [shipmentType, setShipmentType] = useState("PARCEL");
  const [customerMode, setCustomerMode] = useState("existing");
  const [customerId, setCustomerId] = useState("");
  const [customer, setCustomer] = useState({ name: "", email: "", phone: "", company: "" });
  const [originCountryCode, setOriginCountryCode] = useState("US");
  const [originCity, setOriginCity] = useState("New York");
  const [destCountryCode, setDestCountryCode] = useState("GB");
  const [destCity, setDestCity] = useState("London");
  const [eta, setEta] = useState("");
  const [carrier, setCarrier] = useState("");
  const [referenceNumber, setReferenceNumber] = useState("");
  const [saving, setSaving] = useState(false);
  const [created, setCreated] = useState(null);
  const [error, setError] = useState("");

  const navigate = useNavigate();

  useEffect(() => {
    apiFetch("/api/countries").then((d) => setCountries(d.countries || [])).catch(() => setCountries([]));
    apiFetch("/api/admin/customers", { token: auth.token }).then((d) => setCustomers(d.items || [])).catch(() => setCustomers([]));
  }, [auth.token]);

  const submit = async () => {
    setSaving(true);
    setError("");
    try {
      const payload = {
        mode,
        shipmentType,
        originCountryCode,
        originCity,
        destCountryCode,
        destCity,
        eta: eta ? new Date(eta).toISOString() : null,
        carrier: carrier || null,
        referenceNumber: referenceNumber || null,
        ...(customerMode === "existing" ? { customerId } : { customer }),
      };
      const resp = await apiFetch("/api/admin/shipments", { method: "POST", token: auth.token, body: payload });
      setCreated(resp);
    } catch (e) {
      setError(e.message || "Failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <AdminLayout auth={auth}>
      <div data-testid="admin-create-shipment-page">
        <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
          <div>
            <div className="text-xs font-semibold text-slate-500">Create shipment</div>
            <div className="text-2xl font-extrabold text-slate-900" data-testid="admin-create-shipment-title">New Shipment</div>
          </div>
          <SecondaryButton onClick={() => navigate("/admin/shipments")} data-testid="admin-create-shipment-back-button">Back</SecondaryButton>
        </div>

        {created ? (
          <div className="mt-6 rounded-2xl border border-emerald-200 bg-emerald-50 p-6" data-testid="admin-create-success">
            <div className="text-sm font-bold text-emerald-900">Shipment created</div>
            <div className="mt-1 text-sm text-emerald-800">
              Tracking ID: <span className="font-mono font-semibold" data-testid="admin-created-tracking-id">{created.trackingId}</span>
            </div>
            <div className="mt-4 flex flex-wrap gap-3">
              <Link to={`/track/${encodeURIComponent(created.trackingId)}`} data-testid="admin-created-public-track-link">
                <PrimaryButton>Open public tracking <ArrowRightIcon className="h-4 w-4" /></PrimaryButton>
              </Link>
              <Link to={`/admin/shipments/${created.id}`} data-testid="admin-created-admin-detail-link">
                <SecondaryButton>Open admin detail</SecondaryButton>
              </Link>
            </div>
          </div>
        ) : null}

        <div className="mt-6 grid gap-6 rounded-2xl border border-slate-200 bg-white p-6 shadow-sm" data-testid="admin-create-form">
          <div className="grid gap-4 md:grid-cols-2">
            <Field label="Mode" required>
              <Select data-testid="admin-create-mode-select" value={mode} onChange={(e) => setMode(e.target.value)}>
                <option value="AIR">Air</option>
                <option value="SEA">Sea</option>
                <option value="ROAD">Road</option>
              </Select>
            </Field>
            <Field label="Shipment type" required>
              <Select data-testid="admin-create-shipment-type-select" value={shipmentType} onChange={(e) => setShipmentType(e.target.value)}>
                <option value="PARCEL">Parcel</option>
                <option value="FREIGHT">Freight</option>
              </Select>
            </Field>
          </div>

          <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4" data-testid="admin-create-customer-section">
            <div className="flex items-center justify-between">
              <div className="text-sm font-bold text-slate-900">Customer</div>
              <div className="flex gap-2">
                <button
                  className={clsx(
                    "rounded-lg px-3 py-1 text-xs font-bold",
                    customerMode === "existing" ? "bg-slate-900 text-white" : "bg-white text-slate-700 border border-slate-200"
                  )}
                  onClick={() => setCustomerMode("existing")}
                  data-testid="admin-customer-mode-existing"
                  type="button"
                >
                  Existing
                </button>
                <button
                  className={clsx(
                    "rounded-lg px-3 py-1 text-xs font-bold",
                    customerMode === "new" ? "bg-slate-900 text-white" : "bg-white text-slate-700 border border-slate-200"
                  )}
                  onClick={() => setCustomerMode("new")}
                  data-testid="admin-customer-mode-new"
                  type="button"
                >
                  New
                </button>
              </div>
            </div>

            {customerMode === "existing" ? (
              <div className="mt-4">
                <Select data-testid="admin-create-customer-select" value={customerId} onChange={(e) => setCustomerId(e.target.value)}>
                  <option value="">Select customer</option>
                  {customers.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name} ({c.email})
                    </option>
                  ))}
                </Select>
              </div>
            ) : (
              <div className="mt-4 grid gap-4 md:grid-cols-2">
                <Field label="Name" required>
                  <Input data-testid="admin-create-customer-name" value={customer.name} onChange={(e) => setCustomer((v) => ({ ...v, name: e.target.value }))} />
                </Field>
                <Field label="Email" required>
                  <Input data-testid="admin-create-customer-email" value={customer.email} onChange={(e) => setCustomer((v) => ({ ...v, email: e.target.value }))} />
                </Field>
                <Field label="Phone">
                  <Input data-testid="admin-create-customer-phone" value={customer.phone} onChange={(e) => setCustomer((v) => ({ ...v, phone: e.target.value }))} />
                </Field>
                <Field label="Company">
                  <Input data-testid="admin-create-customer-company" value={customer.company} onChange={(e) => setCustomer((v) => ({ ...v, company: e.target.value }))} />
                </Field>
              </div>
            )}
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <Field label="Origin country" required>
              <Select data-testid="admin-create-origin-country" value={originCountryCode} onChange={(e) => setOriginCountryCode(e.target.value)}>
                {countries.map((c) => (
                  <option key={c.code} value={c.code}>
                    {c.name} ({c.code})
                  </option>
                ))}
              </Select>
            </Field>
            <Field label="Origin city" required>
              <Input data-testid="admin-create-origin-city" value={originCity} onChange={(e) => setOriginCity(e.target.value)} />
            </Field>
            <Field label="Destination country" required>
              <Select data-testid="admin-create-dest-country" value={destCountryCode} onChange={(e) => setDestCountryCode(e.target.value)}>
                {countries.map((c) => (
                  <option key={c.code} value={c.code}>
                    {c.name} ({c.code})
                  </option>
                ))}
              </Select>
            </Field>
            <Field label="Destination city" required>
              <Input data-testid="admin-create-dest-city" value={destCity} onChange={(e) => setDestCity(e.target.value)} />
            </Field>
          </div>

          <div className="grid gap-4 md:grid-cols-3">
            <Field label="ETA">
              <Input data-testid="admin-create-eta" type="datetime-local" value={eta} onChange={(e) => setEta(e.target.value)} />
            </Field>
            <Field label="Carrier">
              <Input data-testid="admin-create-carrier" value={carrier} onChange={(e) => setCarrier(e.target.value)} placeholder="Optional" />
            </Field>
            <Field label="Reference number">
              <Input data-testid="admin-create-reference" value={referenceNumber} onChange={(e) => setReferenceNumber(e.target.value)} placeholder="Optional" />
            </Field>
          </div>

          {error ? (
            <div className="rounded-xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-800" data-testid="admin-create-error">
              {error}
            </div>
          ) : null}

          <div className="flex gap-3">
            <PrimaryButton onClick={submit} disabled={saving} data-testid="admin-create-submit-button">
              {saving ? "Creating..." : "Create shipment"}
            </PrimaryButton>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}

function AdminShipmentDetail({ auth }) {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // Initialize eventTime with current datetime in local format for datetime-local input
  const getLocalDateTime = () => {
    const now = new Date();
    const offset = now.getTimezoneOffset();
    const local = new Date(now.getTime() - offset * 60 * 1000);
    return local.toISOString().slice(0, 16);
  };

  const [event, setEvent] = useState({ status: "IN_TRANSIT", location: "", notes: "", eventTime: getLocalDateTime() });
  const [adding, setAdding] = useState(false);
  const [addError, setAddError] = useState("");

  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState("");

  const reload = async () => {
    setLoading(true);
    setError("");
    try {
      const d = await apiFetch(`/api/admin/shipments/${id}`, { token: auth.token });
      setData(d);
    } catch (e) {
      setError(e.message || "Failed");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const addEvent = async () => {
    setAdding(true);
    setAddError("");
    try {
      // Use user-provided eventTime or fall back to current time
      const eventTimeISO = event.eventTime
        ? new Date(event.eventTime).toISOString()
        : new Date().toISOString();

      await apiFetch(`/api/admin/shipments/${id}/events`, {
        method: "POST",
        token: auth.token,
        body: { status: event.status, location: event.location, notes: event.notes, eventTime: eventTimeISO },
      });
      setEvent({ status: "IN_TRANSIT", location: "", notes: "", eventTime: getLocalDateTime() });
      await reload();
    } catch (e) {
      setAddError(e.message || "Failed");
    } finally {
      setAdding(false);
    }
  };

  const uploadDoc = async (file, type) => {
    setUploading(true);
    setUploadError("");
    try {
      // get signature
      const sig = await apiFetch("/api/admin/cloudinary/signature?resource_type=raw", { token: auth.token });
      const form = new FormData();
      form.append("file", file);
      form.append("api_key", sig.api_key);
      form.append("timestamp", sig.timestamp);
      form.append("signature", sig.signature);
      form.append("folder", sig.folder);

      const res = await fetch(sig.upload_url, { method: "POST", body: form });
      const up = await res.json();
      if (!res.ok) throw new Error(up.error?.message || "Upload failed");

      await apiFetch(`/api/admin/shipments/${id}/documents`, {
        method: "POST",
        token: auth.token,
        body: {
          type,
          filename: file.name,
          urlOrPath: up.secure_url,
          publicId: up.public_id,
        },
      });

      await reload();
    } catch (e) {
      setUploadError(e.message || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  return (
    <AdminLayout auth={auth}>
      <div data-testid="admin-shipment-detail-page">
        <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
          <div>
            <div className="text-xs font-semibold text-slate-500">Shipment detail</div>
            <div className="text-2xl font-extrabold text-slate-900" data-testid="admin-shipment-detail-title">{id}</div>
          </div>
          {data?.shipment?.trackingId ? (
            <Link to={`/track/${encodeURIComponent(data.shipment.trackingId)}`} data-testid="admin-shipment-copy-public-link">
              <SecondaryButton>Open public tracking</SecondaryButton>
            </Link>
          ) : null}
        </div>

        {loading ? (
          <div className="mt-6 rounded-2xl border border-slate-200 bg-white p-6" data-testid="admin-shipment-detail-loading">
            <div className="h-4 w-52 animate-pulse rounded bg-slate-100" />
          </div>
        ) : error ? (
          <div className="mt-6 rounded-2xl border border-rose-200 bg-rose-50 p-6 text-sm text-rose-800" data-testid="admin-shipment-detail-error">
            {error}
          </div>
        ) : (
          <div className="mt-6 grid gap-6">
            <div className="rounded-2xl border border-slate-200 bg-white p-6" data-testid="admin-shipment-overview">
              <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                <div>
                  <div className="text-xs font-semibold text-slate-500">Tracking ID</div>
                  <div className="mt-1 font-mono text-lg font-bold text-slate-900" data-testid="admin-shipment-tracking-id">{data.shipment.trackingId}</div>
                </div>
                <div className="flex items-center gap-2">
                  <Badge status={(data.events?.[0]?.status) || "LABEL_CREATED"} />
                </div>
              </div>
              <div className="mt-5 grid gap-4 md:grid-cols-2">
                <div className="rounded-xl bg-slate-50 p-4">
                  <div className="text-xs font-bold text-slate-600">Route</div>
                  <div className="mt-1 text-sm font-semibold text-slate-900">
                    {data.shipment.originCity} ({data.shipment.originCountryCode}) → {data.shipment.destCity} ({data.shipment.destCountryCode})
                  </div>
                </div>
                <div className="rounded-xl bg-slate-50 p-4">
                  <div className="text-xs font-bold text-slate-600">Customer</div>
                  <div className="mt-1 text-sm font-semibold text-slate-900">{data.customer?.name || "—"}</div>
                  <div className="text-sm text-slate-600">{data.customer?.email || "—"}</div>
                </div>
              </div>
            </div>

            <div className="rounded-2xl border border-slate-200 bg-white p-6" data-testid="admin-shipment-events">
              <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
                <div className="text-sm font-bold text-slate-900">Tracking events</div>
              </div>
              <div className="mt-4">
                <Timeline events={data.events || []} />
              </div>

              <div className="mt-6 grid gap-4 rounded-2xl border border-slate-200 bg-slate-50 p-4" data-testid="admin-add-event-form">
                <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
                  <Field label="Status" required>
                    <Select data-testid="admin-add-event-status" value={event.status} onChange={(e) => setEvent((v) => ({ ...v, status: e.target.value }))}>
                      <option value="LABEL_CREATED">Label Created</option>
                      <option value="PICKED_UP">Picked Up</option>
                      <option value="IN_TRANSIT">In Transit</option>
                      <option value="ARRIVED_AT_HUB">Arrived at Hub</option>
                      <option value="CUSTOMS">Customs</option>
                      <option value="OUT_FOR_DELIVERY">Out for Delivery</option>
                      <option value="DELIVERED">Delivered</option>
                      <option value="EXCEPTION">Exception</option>
                      <option value="PACKAGE_ON_HOLD">Package on Hold</option>
                    </Select>
                  </Field>
                  <Field label="Event Date & Time" required>
                    <Input
                      type="datetime-local"
                      data-testid="admin-add-event-time"
                      value={event.eventTime}
                      onChange={(e) => setEvent((v) => ({ ...v, eventTime: e.target.value }))}
                    />
                  </Field>
                  <Field label="Location" required>
                    <Input data-testid="admin-add-event-location" value={event.location} onChange={(e) => setEvent((v) => ({ ...v, location: e.target.value }))} placeholder="City, Country" />
                  </Field>
                  <Field label="Notes">
                    <Input data-testid="admin-add-event-notes" value={event.notes} onChange={(e) => setEvent((v) => ({ ...v, notes: e.target.value }))} placeholder="Optional" />
                  </Field>
                </div>
                {addError ? (
                  <div className="rounded-xl border border-rose-200 bg-rose-50 p-3 text-sm text-rose-800" data-testid="admin-add-event-error">
                    {addError}
                  </div>
                ) : null}
                <div>
                  <PrimaryButton onClick={addEvent} disabled={adding} data-testid="admin-add-event-submit">
                    {adding ? "Adding..." : "Add event"}
                  </PrimaryButton>
                </div>
              </div>
            </div>

            <div className="rounded-2xl border border-slate-200 bg-white p-6" data-testid="admin-shipment-documents">
              <div className="text-sm font-bold text-slate-900">Documents</div>
              <div className="mt-2 text-sm text-slate-600">Upload B/L, invoice, packing list, or other documents (stored in Cloudinary).</div>

              <div className="mt-4 grid gap-3 rounded-2xl border border-slate-200 bg-slate-50 p-4">
                <div className="grid gap-3 md:grid-cols-3">
                  <Field label="Type" required>
                    <Select data-testid="admin-doc-type-select" defaultValue="OTHER">
                      <option value="BOL">B/L</option>
                      <option value="INVOICE">Invoice</option>
                      <option value="PACKING_LIST">Packing List</option>
                      <option value="OTHER">Other</option>
                    </Select>
                  </Field>
                  <Field label="File" required>
                    <Input data-testid="admin-doc-file-input" type="file" />
                  </Field>
                  <div className="flex items-end">
                    <PrimaryButton
                      data-testid="admin-doc-upload-button"
                      disabled={uploading}
                      onClick={() => {
                        const typeEl = document.querySelector('[data-testid="admin-doc-type-select"]');
                        const fileEl = document.querySelector('[data-testid="admin-doc-file-input"]');
                        const type = typeEl?.value || "OTHER";
                        const file = fileEl?.files?.[0];
                        if (!file) {
                          setUploadError("Please choose a file");
                          return;
                        }
                        uploadDoc(file, type);
                      }}
                    >
                      {uploading ? "Uploading..." : "Upload"} <DocumentArrowUpIcon className="h-4 w-4" />
                    </PrimaryButton>
                  </div>
                </div>
                {uploadError ? (
                  <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800" data-testid="admin-doc-upload-message">
                    {uploadError}
                  </div>
                ) : null}
                <div className="text-xs text-slate-500">
                  Uploaded files are stored in Cloudinary and listed below.
                </div>
              </div>

              <div className="mt-4">
                {(data.documents || []).length === 0 ? (
                  <div className="text-sm text-slate-600" data-testid="admin-documents-empty">No documents yet.</div>
                ) : (
                  <div className="space-y-2" data-testid="admin-documents-list">
                    {data.documents.map((d, idx) => (
                      <div key={d.id} className="flex items-center justify-between rounded-xl border border-slate-200 bg-white px-4 py-3" data-testid={`admin-document-${idx}`}>
                        <div>
                          <div className="text-sm font-semibold text-slate-900">{d.filename}</div>
                          <div className="text-xs text-slate-500">{d.type}</div>
                        </div>
                        <a className="text-sm font-semibold text-harbor-700 hover:text-harbor-800" href={d.urlOrPath} target="_blank" rel="noreferrer" data-testid={`admin-document-link-${idx}`}>
                          Open
                        </a>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}

function AdminQuotes({ auth }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const d = await apiFetch("/api/admin/quotes", { token: auth.token });
      setItems(d.items || []);
    } catch (e) {
      setError(e.message || "Failed");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <AdminLayout auth={auth}>
      <div data-testid="admin-quotes-page">
        <div>
          <div className="text-xs font-semibold text-slate-500">Quotes</div>
          <div className="text-2xl font-extrabold text-slate-900" data-testid="admin-quotes-title">Quote requests</div>
        </div>

        {loading ? (
          <div className="mt-6 rounded-2xl border border-slate-200 bg-white p-6" data-testid="admin-quotes-loading">
            <div className="h-4 w-64 animate-pulse rounded bg-slate-100" />
          </div>
        ) : error ? (
          <div className="mt-6 rounded-2xl border border-rose-200 bg-rose-50 p-6 text-sm text-rose-800" data-testid="admin-quotes-error">
            {error}
          </div>
        ) : (
          <div className="mt-6 overflow-hidden rounded-2xl border border-slate-200 bg-white" data-testid="admin-quotes-table">
            <div className="grid grid-cols-12 gap-2 border-b border-slate-200 bg-slate-50 px-4 py-3 text-xs font-bold text-slate-600">
              <div className="col-span-3">Customer</div>
              <div className="col-span-3">Route</div>
              <div className="col-span-2">Mode</div>
              <div className="col-span-2">Type</div>
              <div className="col-span-2">Status</div>
            </div>
            {items.length === 0 ? (
              <div className="px-4 py-6 text-sm text-slate-600" data-testid="admin-quotes-empty">No quote requests.</div>
            ) : (
              items.map((q, idx) => (
                <Link
                  key={q.id}
                  to={`/admin/quotes/${q.id}`}
                  className="grid grid-cols-12 gap-2 border-b border-slate-100 px-4 py-4 text-sm hover:bg-slate-50"
                  data-testid={`admin-quotes-row-${idx}`}
                >
                  <div className="col-span-3">
                    <div className="font-semibold text-slate-900">{q.name}</div>
                    <div className="text-xs text-slate-500">{q.email}</div>
                  </div>
                  <div className="col-span-3 text-slate-700">{q.originCity} ({q.originCountryCode}) → {q.destCity} ({q.destCountryCode})</div>
                  <div className="col-span-2 text-slate-700">{q.mode}</div>
                  <div className="col-span-2 text-slate-700">{q.shipmentType}</div>
                  <div className="col-span-2"><Badge status={q.status} /></div>
                </Link>
              ))
            )}
          </div>
        )}
      </div>
    </AdminLayout>
  );
}

function moneyFmt(value, currency = "USD") {
  const n = Number(value);
  if (Number.isNaN(n)) return "";
  return new Intl.NumberFormat(undefined, { style: "currency", currency }).format(n);
}

function QuoteInvoiceEditor({ auth, quote, onSaved }) {
  const [subtotal, setSubtotal] = useState(quote.invoice?.subtotal ?? "");
  const [tax, setTax] = useState(quote.invoice?.tax ?? "");
  const [total, setTotal] = useState(quote.invoice?.total ?? "");
  const [currency, setCurrency] = useState(quote.invoice?.currency ?? "USD");

  const [proformaNumber, setProformaNumber] = useState(quote.invoice?.proformaNumber ?? "");

  const [billingName, setBillingName] = useState(quote.invoice?.billingName ?? quote.name ?? "");
  const [billingCompany, setBillingCompany] = useState(quote.invoice?.billingCompany ?? quote.company ?? "");
  const [billingEmail, setBillingEmail] = useState(quote.invoice?.billingEmail ?? quote.email ?? "");
  const [billingAddress, setBillingAddress] = useState(
    quote.invoice?.billingAddress ?? "14 Hudson Yards\nNew York, NY 10001\nUnited States"
  );
  const [notes, setNotes] = useState(quote.invoice?.notes ?? "");

  const [items, setItems] = useState(
    quote.invoice?.items?.length
      ? quote.invoice.items
      : [{ description: `Freight service (${quote.mode})`, qty: 1, unitPrice: subtotal === "" ? "" : Number(subtotal), amount: subtotal === "" ? "" : Number(subtotal) }]
  );

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const save = async () => {
    setSaving(true);
    setError("");
    try {
      const payload = {
        subtotal: subtotal === "" ? null : Number(subtotal),
        tax: tax === "" ? null : Number(tax),
        total: total === "" ? null : Number(total),
        currency,
        proformaNumber: proformaNumber || null,
        billingName: billingName || null,
        billingCompany: billingCompany || null,
        billingEmail: billingEmail || null,
        billingAddress: billingAddress || null,
        notes: notes || null,
        items: (items || []).map((it) => ({
          description: it.description,
          qty: Number(it.qty) || 0,
          unitPrice: it.unitPrice === "" ? null : Number(it.unitPrice),
          amount: it.amount === "" ? null : Number(it.amount),
        })),
      };
      await apiFetch(`/api/admin/quotes/${quote.id}/invoice`, { method: "PATCH", token: auth.token, body: payload });
      await onSaved();
    } catch (e) {
      setError(e.message || "Failed");
      setSaving(false);
    }
  };

  const recalc = (nextItems, nextTax = tax) => {
    const sub = (nextItems || []).reduce((acc, it) => {
      const amt = Number(it.amount);
      if (Number.isNaN(amt)) return acc;
      return acc + amt;
    }, 0);
    const t = Number(nextTax);
    const tVal = Number.isNaN(t) ? 0 : t;
    setSubtotal(sub.toFixed(2));
    setTotal((sub + tVal).toFixed(2));
  };

  useEffect(() => {
    // keep totals in sync when line items change
    recalc(items, tax);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const updateItem = (idx, patch) => {
    const next = items.map((it, i) => (i === idx ? { ...it, ...patch } : it));
    // auto-calc amount if qty/unitPrice changed
    const it = next[idx];
    const qtyN = Number(it.qty);
    const unitN = Number(it.unitPrice);
    if (!Number.isNaN(qtyN) && !Number.isNaN(unitN)) {
      next[idx] = { ...it, amount: (qtyN * unitN).toFixed(2) };
    }
    setItems(next);
    recalc(next, tax);
  };

  const addItem = () => {
    const next = [...items, { description: "", qty: 1, unitPrice: "", amount: "" }];
    setItems(next);
  };

  const removeItem = (idx) => {
    const next = items.filter((_, i) => i !== idx);
    setItems(next.length ? next : [{ description: "", qty: 1, unitPrice: "", amount: "" }]);
    recalc(next, tax);
  };

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm" data-testid="quote-invoice-editor">
      <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
        <div>
          <div className="text-xs font-semibold text-slate-500">Invoice</div>
          <div className="mt-1 text-lg font-extrabold text-slate-900">Quotation / Proforma</div>
          <div className="mt-1 text-sm text-slate-600">Fill the fields below, then generate a PDF.</div>
        </div>
        <div className="flex gap-2">
          <SecondaryButton
            type="button"
            onClick={async () => {
              setError("");
              try {
                // Auto-save invoice fields before generating the PDF so amounts always show.
                const payload = {
                  subtotal: subtotal === "" ? null : Number(subtotal),
                  tax: tax === "" ? null : Number(tax),
                  total: total === "" ? null : Number(total),
                  currency,
                  proformaNumber: proformaNumber || null,
                  billingName: billingName || null,
                  billingCompany: billingCompany || null,
                  billingEmail: billingEmail || null,
                  billingAddress: billingAddress || null,
                  notes: notes || null,
                  items: (items || []).map((it) => ({
                    description: it.description,
                    qty: Number(it.qty) || 0,
                    unitPrice: it.unitPrice === "" ? null : Number(it.unitPrice),
                    amount: it.amount === "" ? null : Number(it.amount),
                  })),
                };
                await apiFetch(`/api/admin/quotes/${quote.id}/invoice`, { method: "PATCH", token: auth.token, body: payload });

                const res = await fetch(`${BACKEND_URL}/api/admin/quotes/${quote.id}/invoice.pdf`, {
                  headers: {
                    Authorization: `Bearer ${auth.token}`,
                  },
                });
                if (!res.ok) {
                  const txt = await res.text();
                  throw new Error(txt || "Failed to generate PDF");
                }
                const blob = await res.blob();
                const objectUrl = window.URL.createObjectURL(blob);
                const a = document.createElement("a");
                a.href = objectUrl;
                a.download = `primeharbor-quote-${quote.id}.pdf`;
                document.body.appendChild(a);
                a.click();
                a.remove();
                window.URL.revokeObjectURL(objectUrl);
              } catch (e) {
                setError(e.message || "Failed to generate PDF");
              }
            }}
            data-testid="quote-invoice-pdf-button"
          >
            Generate PDF
          </SecondaryButton>
          <PrimaryButton type="button" onClick={save} disabled={saving} data-testid="quote-invoice-save-button">
            {saving ? "Saving..." : "Save"}
          </PrimaryButton>
        </div>
      </div>

      {error ? (
        <div className="mt-4 rounded-xl border border-rose-200 bg-rose-50 p-3 text-sm text-rose-800" data-testid="quote-invoice-error">{error}</div>
      ) : null}

      <div className="mt-6 grid gap-4 md:grid-cols-3">
        <Field label="Currency">
          <Select value={currency} onChange={(e) => setCurrency(e.target.value)} data-testid="quote-invoice-currency">
            <option value="USD">USD</option>
            <option value="EUR">EUR</option>
            <option value="GBP">GBP</option>
          </Select>
        </Field>
        <Field label="Proforma #">
          <Input value={proformaNumber} onChange={(e) => setProformaNumber(e.target.value)} data-testid="quote-invoice-proforma-number" placeholder="PH-PI-000123" />
        </Field>
        <Field label="Tax (amount)" required>
          <Input
            value={tax}
            onChange={(e) => {
              setTax(e.target.value);
              recalc(items, e.target.value);
            }}
            inputMode="decimal"
            data-testid="quote-invoice-tax"
          />
        </Field>
      </div>

      <div className="mt-4 rounded-2xl border border-slate-200 bg-white p-5" data-testid="quote-line-items">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-xs font-semibold text-slate-500">Invoice</div>
            <div className="text-sm font-extrabold text-slate-900">Line items</div>
          </div>
          <SecondaryButton type="button" onClick={addItem} data-testid="add-line-item-button">
            Add item
          </SecondaryButton>
        </div>

        <div className="mt-4 overflow-x-auto">
          <div className="min-w-[900px]">
            <div className="grid grid-cols-12 gap-2 border-b border-slate-200 bg-slate-50 px-3 py-2 text-xs font-bold text-slate-600">
              <div className="col-span-6">Description</div>
              <div className="col-span-2 text-right">Qty</div>
              <div className="col-span-2 text-right">Unit</div>
              <div className="col-span-2 text-right">Amount</div>
            </div>
            {(items || []).map((it, idx) => (
              <div key={idx} className="grid grid-cols-12 gap-2 border-b border-slate-100 px-3 py-3 text-sm">
                <div className="col-span-6">
                  <Input
                    value={it.description}
                    onChange={(e) => updateItem(idx, { description: e.target.value })}
                    data-testid={`line-item-desc-${idx}`}
                    placeholder="e.g., Ocean freight (FCL)"
                  />
                </div>
                <div className="col-span-2">
                  <Input
                    value={it.qty}
                    onChange={(e) => updateItem(idx, { qty: e.target.value })}
                    inputMode="decimal"
                    className="text-right"
                    data-testid={`line-item-qty-${idx}`}
                  />
                </div>
                <div className="col-span-2">
                  <Input
                    value={it.unitPrice}
                    onChange={(e) => updateItem(idx, { unitPrice: e.target.value })}
                    inputMode="decimal"
                    className="text-right"
                    data-testid={`line-item-unit-${idx}`}
                  />
                </div>
                <div className="col-span-2 flex items-center gap-2">
                  <Input value={it.amount} onChange={(e) => updateItem(idx, { amount: e.target.value })} inputMode="decimal" className="text-right" data-testid={`line-item-amount-${idx}`} />
                  <button
                    type="button"
                    className="rounded-lg px-2 py-2 text-xs font-semibold text-rose-700 hover:bg-rose-50"
                    onClick={() => removeItem(idx)}
                    data-testid={`line-item-remove-${idx}`}
                  >
                    Remove
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-4 grid gap-3 md:grid-cols-3">
          <div className="rounded-xl border border-slate-200 bg-slate-50 p-3">
            <div className="text-xs font-semibold text-slate-500">Subtotal</div>
            <div className="mt-1 text-sm font-extrabold text-slate-900" data-testid="quote-subtotal-preview">{moneyFmt(subtotal || 0, currency)}</div>
          </div>
          <div className="rounded-xl border border-slate-200 bg-slate-50 p-3">
            <div className="text-xs font-semibold text-slate-500">Tax</div>
            <div className="mt-1 text-sm font-extrabold text-slate-900" data-testid="quote-tax-preview">{moneyFmt(tax || 0, currency)}</div>
          </div>
          <div className="rounded-xl border border-slate-200 bg-harbor-50 p-3">
            <div className="text-xs font-semibold text-slate-600">Total</div>
            <div className="mt-1 text-sm font-extrabold text-slate-900" data-testid="quote-total-preview">{moneyFmt(total || 0, currency)}</div>
          </div>
        </div>
      </div>

      <div className="mt-4 grid gap-4 md:grid-cols-3">
        <Field label="Billing name">
          <Input value={billingName} onChange={(e) => setBillingName(e.target.value)} data-testid="quote-invoice-billing-name" />
        </Field>
        <Field label="Billing company">
          <Input value={billingCompany} onChange={(e) => setBillingCompany(e.target.value)} data-testid="quote-invoice-billing-company" />
        </Field>
        <Field label="Total" required>
          <Input value={total} onChange={(e) => setTotal(e.target.value)} inputMode="decimal" data-testid="quote-invoice-total" />
        </Field>
      </div>

      <div className="mt-4 grid gap-4 md:grid-cols-2">
        <Field label="Billing email">
          <Input value={billingEmail} onChange={(e) => setBillingEmail(e.target.value)} data-testid="quote-invoice-billing-email" />
        </Field>
        <Field label="Billing address">
          <TextArea value={billingAddress} onChange={(e) => setBillingAddress(e.target.value)} rows={3} data-testid="quote-invoice-billing-address" />
        </Field>
      </div>

      <div className="mt-4">
        <Field label="Notes">
          <TextArea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} data-testid="quote-invoice-notes" />
        </Field>
      </div>
    </div>
  );
}

function AdminQuoteDetail({ auth }) {
  const { id } = useParams();
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const d = await apiFetch(`/api/admin/quotes/${id}`, { token: auth.token });
      setQuote(d);
    } catch (e) {
      setError(e.message || "Failed");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  return (
    <AdminLayout auth={auth}>
      <div data-testid="admin-quote-detail-page">
        <div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
          <div>
            <div className="text-xs font-semibold text-slate-500">Quotes</div>
            <div className="text-2xl font-extrabold text-slate-900" data-testid="admin-quote-detail-title">Quote request</div>
            <div className="mt-1 text-sm text-slate-600 font-mono">{id}</div>
          </div>
          <div className="flex gap-2">
            <Link to="/admin/quotes"><SecondaryButton>Back</SecondaryButton></Link>
          </div>
        </div>

        {loading ? (
          <div className="mt-6 rounded-2xl border border-slate-200 bg-white p-6" data-testid="admin-quote-detail-loading">
            <div className="h-4 w-64 animate-pulse rounded bg-slate-100" />
          </div>
        ) : error ? (
          <div className="mt-6 rounded-2xl border border-rose-200 bg-rose-50 p-6 text-sm text-rose-800" data-testid="admin-quote-detail-error">
            {error}
          </div>
        ) : !quote ? null : (
          <div className="mt-6 grid gap-6 lg:grid-cols-[1fr_420px]">
            <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm" data-testid="admin-quote-detail-card">
              <div className="text-sm font-extrabold text-slate-900">Request details</div>
              <div className="mt-4 grid gap-4 md:grid-cols-2 text-sm">
                <div>
                  <div className="text-xs font-semibold text-slate-500">Customer</div>
                  <div className="mt-1 font-semibold text-slate-900">{quote.name}</div>
                  <div className="text-slate-700">{quote.email}</div>
                  {quote.company ? <div className="text-slate-700">{quote.company}</div> : null}
                </div>
                <div>
                  <div className="text-xs font-semibold text-slate-500">Route</div>
                  <div className="mt-1 font-semibold text-slate-900">
                    {quote.originCity} ({quote.originCountryCode}) → {quote.destCity} ({quote.destCountryCode})
                  </div>
                  <div className="text-slate-700">Mode: {quote.mode} • Type: {quote.shipmentType}</div>
                </div>
                <div>
                  <div className="text-xs font-semibold text-slate-500">Cargo</div>
                  <div className="mt-1 text-slate-700">{quote.cargoDescription}</div>
                </div>
                <div>
                  <div className="text-xs font-semibold text-slate-500">Meta</div>
                  <div className="mt-1 text-slate-700">Weight: {quote.weightKg} kg</div>
                  {quote.incoterms ? <div className="text-slate-700">Incoterms: {quote.incoterms}</div> : null}
                </div>
              </div>
            </div>

            <div>
              <QuoteInvoiceEditor auth={auth} quote={quote} onSaved={load} />
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}


function AdminCustomers({ auth }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    apiFetch("/api/admin/customers", { token: auth.token })
      .then((d) => setItems(d.items || []))
      .catch((e) => setError(e.message || "Failed"))
      .finally(() => setLoading(false));
  }, [auth.token]);

  return (
    <AdminLayout auth={auth}>
      <div data-testid="admin-customers-page">
        <div>
          <div className="text-xs font-semibold text-slate-500">Customers</div>
          <div className="text-2xl font-extrabold text-slate-900" data-testid="admin-customers-title">Customers</div>
        </div>

        {loading ? (
          <div className="mt-6 rounded-2xl border border-slate-200 bg-white p-6" data-testid="admin-customers-loading">
            <div className="h-4 w-64 animate-pulse rounded bg-slate-100" />
          </div>
        ) : error ? (
          <div className="mt-6 rounded-2xl border border-rose-200 bg-rose-50 p-6 text-sm text-rose-800" data-testid="admin-customers-error">
            {error}
          </div>
        ) : (
          <div className="mt-6 overflow-hidden rounded-2xl border border-slate-200 bg-white" data-testid="admin-customers-table">
            <div className="grid grid-cols-12 gap-2 border-b border-slate-200 bg-slate-50 px-4 py-3 text-xs font-bold text-slate-600">
              <div className="col-span-4">Name</div>
              <div className="col-span-4">Email</div>
              <div className="col-span-4">Company</div>
            </div>
            {items.map((c, idx) => (
              <div key={c.id} className="grid grid-cols-12 gap-2 border-b border-slate-100 px-4 py-3 text-sm" data-testid={`admin-customers-row-${idx}`}>
                <div className="col-span-4 font-semibold text-slate-900">{c.name}</div>
                <div className="col-span-4 text-slate-700">{c.email}</div>
                <div className="col-span-4 text-slate-700">{c.company || "—"}</div>
              </div>
            ))}
          </div>
        )}
      </div>
    </AdminLayout>
  );
}

function AdminSettings({ auth }) {
  return (
    <AdminLayout auth={auth}>
      <div data-testid="admin-settings-page">
        <div className="text-xs font-semibold text-slate-500">Settings</div>
        <div className="text-2xl font-extrabold text-slate-900" data-testid="admin-settings-title">Settings</div>
        <div className="mt-6 rounded-2xl border border-slate-200 bg-white p-6 text-sm text-slate-600" data-testid="admin-settings-placeholder">
          Profile + password change placeholder for MVP.
        </div>
      </div>
    </AdminLayout>
  );
}

function AdminRouter({ auth }) {
  // NOTE: This component is mounted at route "/admin/*".
  // Therefore, child routes must be relative (no leading "/").
  return (
    <Routes>
      <Route
        index
        element={
          <RequireAuth auth={auth}>
            <AdminDashboard auth={auth} />
          </RequireAuth>
        }
      />
      <Route
        path="shipments"
        element={
          <RequireAuth auth={auth}>
            <AdminShipmentsList auth={auth} />
          </RequireAuth>
        }
      />
      <Route
        path="shipments/new"
        element={
          <RequireAuth auth={auth}>
            <AdminCreateShipment auth={auth} />
          </RequireAuth>
        }
      />
      <Route
        path="shipments/:id"
        element={
          <RequireAuth auth={auth}>
            <AdminShipmentDetail auth={auth} />
          </RequireAuth>
        }
      />
      <Route
        path="quotes"
        element={
          <RequireAuth auth={auth}>
            <AdminQuotes auth={auth} />
          </RequireAuth>
        }
      />
      <Route
        path="quotes/:id"
        element={
          <RequireAuth auth={auth}>
            <AdminQuoteDetail auth={auth} />
          </RequireAuth>
        }
      />
      <Route
        path="customers"
        element={
          <RequireAuth auth={auth}>
            <AdminCustomers auth={auth} />
          </RequireAuth>
        }
      />
      <Route
        path="settings"
        element={
          <RequireAuth auth={auth}>
            <AdminSettings auth={auth} />
          </RequireAuth>
        }
      />
      <Route path="*" element={<AdminLoginPage auth={auth} />} />
    </Routes>
  );
}

function App() {
  const auth = useAuth();

  const warning = useMemo(() => {
    if (BACKEND_URL) return "";
    return "Missing REACT_APP_BACKEND_URL in /app/frontend/.env. The UI will load but API calls will fail.";
  }, []);

  return (
    <BrowserRouter>
      <div className="min-h-screen bg-white text-slate-900">
        <TopNav auth={auth} />

        {warning ? (
          <div className="border-b border-amber-200 bg-amber-50" data-testid="global-warning">
            <Container className="py-2 text-xs font-semibold text-amber-900">{warning}</Container>
          </div>
        ) : null}

        <Routes>
          {/* Public */}
          <Route path="/" element={<HomePage />} />
          <Route path="/services" element={<ServicesPage />} />
          <Route path="/services/air-freight" element={<AirFreightPage />} />
          <Route path="/services/sea-freight" element={<SeaFreightPage />} />
          <Route path="/services/road-freight" element={<RoadFreightPage />} />
          <Route path="/services/customs" element={<CustomsPage />} />
          <Route path="/services/warehousing" element={<WarehousingPage />} />
          {/* /services/secure-admin removed */}
          <Route path="/track" element={<TrackSearchPage />} />
          <Route path="/track/:trackingId" element={<TrackResultPage />} />
          <Route path="/quote" element={<QuotePage />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="/terms" element={<TermsPage />} />

          {/* Admin */}
          <Route path="/admin/*" element={<AdminRouter auth={auth} />} />

          {/* Fallback */}
          <Route path="/*" element={<HomePage />} />
        </Routes>

        <Footer />
      </div>
    </BrowserRouter>
  );
}

export default App;
